import React, { useState, useEffect, useRef, useCallback } from "react";
import { Card, Table, Button, Typography, message, Tooltip, Select, Tag, Modal } from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ArrowRightOutlined,
  SettingOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { useTender } from "../../../contexts/TenderContext";
import { useAuth } from "../../../hooks/useAuth";
import { useScrollToTop } from "../../../hooks/useScrollToTop";
import MainLayout from "../../layout/MainLayout";
import { useExcelImportExport } from "../../../hooks/useExcelImportExport";
import { ExcelImportExport } from "../../common/ExcelImportExport";
import { ExcelColumn } from "../../../utils/excelUtils";
import { COLORS } from "../../../utils/constants";
import { useNavigate } from "react-router-dom";
import { prepareTenderForAPI } from "../../../utils/storage";
import { useCreateBillOfQuantities } from "../../../hooks/useBillOfQuantities";
import { forceSaveBothBoqEndpoints } from "../../../utils/boqVersionUtils";
import { useItemLibrary } from "../../../hooks/useItemLibrary";
import { useGroundworksTotalCalculations } from "../../../hooks/useGroundworksTotalCalculations";
import NumberInput from "../../common/NumberInput";
import BufferedTextInput from "../../common/BufferedTextInput";
// import { useChangeDetection } from "../../../hooks/useChangeDetection";
import { useTableRenderDeferral } from "../../../hooks/useTableRenderDeferral";
import TableLoadingPlaceholder from "../../common/TableLoadingPlaceholder";
import DeleteConfirmationModal from "../../common/DeleteConfirmationModal";
import { ColumnConfigModal } from "../../common/ColumnConfigModal";
import {
  createColumnTitleWithReference,
  getRecommendedColumnWidth,
  ALL_REFERENCES,
} from "../../../utils/cutFillsReferenceMapping";
import { addColumnCodes } from '@/utils/addColumnCodes';
import { isDuplicatePlotNo } from '@/utils/plotNoValidation';
import { FoundationRow } from '../../../types/tender.types';
import { normaliseDecimalMmInLabel } from "../../../utils/dimensionLabel";
import { geoToberWallFieldToSpecId, geoToberWallSpecifications } from '../../../mock/geo-tober-wall-build-up';
import { wallFieldToSpecId, wallSpecifications } from '../../../mock/walls-section-build-up';
import { useGetRetainingWallsBuildUp } from '../../../hooks/useRetainingWallsBuildUp';
import { getNumericBoqVersion } from '../../../utils/boqVersionPrefix';
import { computeSoilManagementDerivedCutFillValues } from '../../../utils/groundworksBoqSummaryComputer';

const { Title, Text } = Typography;

// Excel column definitions for export/import
const excelColumns: ExcelColumn[] = [
  { key: "plotNo", title: "Plot No" },
  { key: "housetype", title: "House type" },
  { key: "foundationType", title: "Foundation Type" },
  { key: "vegStrip", title: "Site strip to remove vegetation" },
  { key: "topsoilStrip", title: "Excavate to strip topsoil, depth n.e. xxmm" },
  { key: "topsoilDisposalOnsite", title: "Topsoil Disposal onsite" },
  { key: "topsoilDisposalOffsite", title: "Topsoil Disposal offsite" },
  { key: "cutFounds", title: "Excavate to reduce level and deposit on site" },
  { key: "fillFounds", title: "Fill to make up levels with site won sub-soil" },
  { key: "cutPaths", title: "Excavate to reduce level and deposit on site" },
  { key: "fillPaths", title: "Fill to make up levels with site won sub-soil" },
  { key: "cutGarden", title: "Excavate to reduce level and deposit on site" },
  { key: "fillGarden", title: "Fill to make up levels with site won sub-soil" },
  { key: "cutDrives", title: "Excavate to reduce level and deposit on site" },
  { key: "fillDrives", title: "Fill to make up levels with site won sub-soil" },
  { key: "fillToMakeUp6F2", title: "Fill to make up levels with 6F2" },
];

interface CutFillRow {
  key: string;
  plotNo: string | number; // Allow both string and number
  housetype: string | number; // Allow both string and number
  foundationType: string | number; // Allow both string and number
  plotType?: "plot" | "garage"; // Optional for backward-compat with rows imported before the toggle existed; default "plot"
  vegStrip: number;
  topsoilStrip: number;
  topsoilDisposalOnsite: number;
  topsoilDisposalOffsite: number;
  cutFounds: number;
  fillFounds: number;
  cutPaths: number;
  fillPaths: number;
  cutGarden: number;
  fillGarden: number;
  cutDrives: number;
  fillDrives: number;
  fillToMakeUp6F2: number;
}

interface CutFillsTableViewProps {
  isLoading: boolean;
  dataSource: CutFillRow[];
  columns: any[];
  rowSelection: NonNullable<React.ComponentProps<typeof Table>["rowSelection"]>;
  scroll: { x: number; y?: number };
  sticky: { offsetHeader: number };
  renderSummary: () => React.ReactNode;
}

// Wraps the Site Clearance / Cut & Fill <Table>s so React.memo can bail out
// of reconciling one entirely when only `activeTab` changes and none of its
// OWN props did - which is what makes switching back to an already-mounted
// tab feel instant (see the `mountedTabs` comment in CutFills below for why
// these tables stay mounted, and each prop's own memoization above for why
// its identity stays stable across a plain tab switch). Without this
// memoization, keeping both tables mounted alone doesn't help - the parent
// component re-rendering on every `activeTab` change would still reconcile
// both tables' full row/cell trees even while one is `display: none`.
const CutFillsTableView = React.memo(function CutFillsTableView({
  isLoading,
  dataSource,
  columns,
  rowSelection,
  scroll,
  sticky,
  renderSummary,
}: CutFillsTableViewProps) {
  if (isLoading) return <TableLoadingPlaceholder />;
  return (
    <Table
      dataSource={dataSource}
      columns={columns}
      pagination={false}
      rowSelection={rowSelection}
      scroll={scroll}
      sticky={sticky}
      size="middle"
      bordered
      className="cut-fills-table"
      summary={renderSummary}
    />
  );
});

const CutFills: React.FC = () => {
  // Scroll to top on page load
  useScrollToTop();

  const { state: tender, updateField, saveTender } = useTender();
  const { user } = useAuth();
  const navigate = useNavigate();
  const createBoqMutation = useCreateBillOfQuantities();

  const tenderName = tender.projectName || '';
  // "currentBoqVersion" can be stored prefixed (e.g. "GWKS-1"/"RS-1" — set by
  // /version-history) — getNumericBoqVersion strips that down to a plain number.
  // Without this, a prefixed string fed straight into useGetRetainingWallsBuildUp's
  // `effectiveVersion >= 1` check coerces to `NaN >= 1` (false), silently disabling the
  // query and understating the Walls contribution to Subsoil Management's quantity.
  const boqVersionNumber = useRef<number>((() => {
    const raw = localStorage.getItem("currentBoqVersion");
    const parsed = raw ? getNumericBoqVersion((JSON.parse(raw) as { version?: number | string })?.version ?? 1) : 1;
    return parsed === 0 ? 1 : parsed;
  })()).current;
  const { data: retWallsBuildUp } = useGetRetainingWallsBuildUp(tenderName, boqVersionNumber, { enabled: !!tenderName });

  // Format column title to display field name and unit separately
  const formatColumnTitle = useCallback(
    (baseTitle: string): React.ReactNode => {
      const unitMatch = baseTitle.match(/\s+(m[²³]|m|Nr|t)$/);
      let titleWithoutUnit = baseTitle;
      let unit = "";

      if (unitMatch) {
        unit = unitMatch[1];
        titleWithoutUnit = baseTitle.replace(/\s+(m[²³]|m|Nr|t)$/, "");
      }

      if (unit) {
        return (
          <div className="text-center">
            <Tooltip title={titleWithoutUnit}>
              <div className="gw-header-title">{titleWithoutUnit}</div>
            </Tooltip>
            <div className="gw-header-unit text-xs text-gray-500">{unit}</div>
          </div>
        );
      }
      return (
        <Tooltip title={baseTitle}>
          <div className="gw-header-title">{baseTitle}</div>
        </Tooltip>
      );
    },
    [],
  );

  // Get checkbox states for calculations - same logic as GroundworksReview
  const getCheckboxState = (key: string, fallbackPercent: number): boolean => {
    const saved = localStorage.getItem(key);
    if (saved !== null) {
      return JSON.parse(saved);
    }
    return fallbackPercent > 0;
  };

  const preliminariesPercent =
    tender.globalSettings?.groundworks?.preliminaries || 0;
  const ohpPercent = tender.globalSettings?.groundworks?.overheadAndProfit || 0;
  const discountPercent = tender.globalSettings?.groundworks?.discount || 0;

  const includePreliminaries = getCheckboxState(
    "groundworks_includePreliminariesDisplay",
    preliminariesPercent,
  );
  const includeOhp = getCheckboxState(
    "groundworks_includeOhpDisplay",
    ohpPercent,
  );
  const includeDiscount = getCheckboxState(
    "groundworks_includeDiscountDisplay",
    discountPercent,
  );

  // Use the EXACT same hook as GroundworksReview for calculations
  const groundworksTotals = useGroundworksTotalCalculations({
    includePreliminaries,
    includeOhp,
    includeDiscount,
  });
  const { data: itemLibraryData } = useItemLibrary(tender.region || "");

  const [data, setData] = useState<CutFillRow[]>([]);
  const [plotTypeFilter, setPlotTypeFilter] = useState<"plots" | "garages">("plots");
  const [activeTab, setActiveTab] = useState<string>("site-clearance");

  // Site Clearance / Cut & Fill previously fully unmounted/remounted their
  // <Table> on every tab switch (`{activeTab === "x" && <Table key={tableKey}
  // .../>}`), so every switch re-paid the full mount cost (AntD's Table setup,
  // ResizeObserver wiring, column width measurement, sticky positioning) for a
  // table that may hold hundreds of per-plot rows - that remount cost, not any
  // missing memoization, is what made tab switching feel slow/"data slowly
  // fills in" even after the data/column memoization above.
  //
  // Both tables are mounted UPFRONT (not lazily on first visit) - `mountedTabs`
  // starts with both keys already in it and nothing ever adds to it later -
  // so the one-time mount cost is paid once on page load, and every tab
  // switch after that (including the very first one) is a pure CSS `display`
  // toggle with zero React/AntD mount work. This trades a bit of extra
  // up-front load time for tab switches that are never slow, not even once -
  // an earlier version of this fix mounted each table lazily on its first
  // visit instead, which made the FIRST switch to a not-yet-visited tab still
  // pay the full mount cost (with a one-frame loading placeholder covering
  // it); this is deliberately more eager than that.
  const [mountedTabs] = useState<Set<string>>(
    () => new Set(["site-clearance", "cut-fill"]),
  );

  // One `useTableRenderDeferral` per table (both fire once, together, on
  // page load, covering each table's real one-time mount) so a tender with a
  // lot of plots doesn't make the initial page load look frozen while both
  // tables' first layout blocks the main thread - see the hook's own doc
  // comment. Once both flip false (moments after mount) they never fire
  // again, since `mountedTabs` is fixed for the component's lifetime.
  const isSiteClearanceTableLoading = useTableRenderDeferral([
    mountedTabs.has("site-clearance"),
  ]);
  const isCutFillTableLoading = useTableRenderDeferral([
    mountedTabs.has("cut-fill"),
  ]);

  // The Table's `sticky.offsetHeader` was previously computed inline in JSX
  // via document.getElementsByClassName(...).getBoundingClientRect() on
  // *every* render - a forced synchronous layout reflow on every keystroke in
  // any cell (each edit calls setData, re-rendering this component). The
  // sticky header's actual height only changes when its contents change
  // (Configure Headers button / View filter show/hide per `activeTab`) or the
  // viewport resizes - so measure it only in those cases instead of every
  // render, cutting out per-keystroke layout thrashing entirely.
  //
  // Kept as two SEPARATE state values (one per table), not one shared value,
  // because the sticky header's height genuinely differs between the two tabs
  // (Configure Headers button is shown on site-clearance, not on cut-fill) -
  // a single shared value would update on every tab switch, which would blow
  // away both tables' React.memo prop-equality below (see CutFillsTableView)
  // and force the *hidden* table to re-render too. Each tab's own value only
  // updates while that tab is actually active, so switching to a tab updates
  // only its own (about-to-be-visible) table's props - the other, now-hidden
  // table's props stay referentially identical and it skips re-rendering
  // entirely.
  const [siteClearanceStickyOffset, setSiteClearanceStickyOffset] = useState(144);
  const [cutFillStickyOffset, setCutFillStickyOffset] = useState(144);
  useEffect(() => {
    if (activeTab !== "site-clearance" && activeTab !== "cut-fill") return;
    const measure = () => {
      const height = document
        .getElementsByClassName("cut-fills-sticky-header")?.[0]
        ?.getBoundingClientRect?.()?.height;
      const next = 144 + (height || 0);
      if (activeTab === "site-clearance") setSiteClearanceStickyOffset(next);
      else setCutFillStickyOffset(next);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [activeTab]);
  const siteClearanceSticky = React.useMemo(
    () => ({ offsetHeader: siteClearanceStickyOffset }),
    [siteClearanceStickyOffset],
  );
  const cutFillSticky = React.useMemo(
    () => ({ offsetHeader: cutFillStickyOffset }),
    [cutFillStickyOffset],
  );

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<string | null>(null);
  const [keysToDelete, setKeysToDelete] = useState<string[]>([]);
  const [selectedCutFillKeys, setSelectedCutFillKeys] = useState<React.Key[]>([]);

  // rowSelection is intentionally the SAME object for both tables (selection
  // is genuinely shared state - selecting a row in one tab's table should
  // show as selected in the other too), memoized so its identity is stable
  // across renders that don't touch selection (e.g. a plain tab switch).
  const rowSelectionConfig = React.useMemo(
    () => ({
      selectedRowKeys: selectedCutFillKeys,
      onChange: setSelectedCutFillKeys,
      columnWidth: 48,
    }),
    [selectedCutFillKeys],
  );

  // Configure Headers state - prefix values for dynamic column headers
  const [prefixValues, setPrefixValues] = useState<Record<string, string>>({});
  const [tempPrefixValues, setTempPrefixValues] = useState<Record<string, string>>({});
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const prevConfigModalVisibleRef = useRef(configModalVisible);

  // Topsoil Management state (Site Clearance Configure Headers)
  const [topsoilManagement, setTopsoilManagement] = useState<string>("");
  const [topsoilSubOption, setTopsoilSubOption] = useState<string>("");
  const [tempTopsoilManagement, setTempTopsoilManagement] = useState<string>("");
  const [tempTopsoilSubOption, setTempTopsoilSubOption] = useState<string>("");

  // Soil Management tab state
  const [soilConfigModalVisible, setSoilConfigModalVisible] = useState(false);
  const [subsoilManagement, setSubsoilManagement] = useState<string>("");
  const [subsoilSubOption, setSubsoilSubOption] = useState<string>("");
  const [subsoilDisposalClassification, setSubsoilDisposalClassification] = useState<string>("");
  const [tempSubsoilManagement, setTempSubsoilManagement] = useState<string>("");
  const [tempSubsoilSubOption, setTempSubsoilSubOption] = useState<string>("");
  const [tempSubsoilDisposalClassification, setTempSubsoilDisposalClassification] = useState<string>("");

  // Soil Management summary data (mapped from Excel)
  interface SoilManagementData {
    foundationsCutArisings: number;
    foundationsFill: number;
    slabPathsCut: number;
    slabPathsFill: number;
    gardensCut: number;
    gardensFill: number;
    drivesCut: number;
    drivesFill: number;
    foundationsArisingsCut: number;
    foundationsArisingsFill: number;
    plotDrainageCut: number;
    plotDrainageFill: number;
    wallsCut: number;
    wallsFill: number;
    plotServicesCut: number;
    plotServicesFill: number;
  }
  const emptySoilData: SoilManagementData = {
    foundationsCutArisings: 0, foundationsFill: 0,
    slabPathsCut: 0, slabPathsFill: 0,
    gardensCut: 0, gardensFill: 0,
    drivesCut: 0, drivesFill: 0,
    foundationsArisingsCut: 0, foundationsArisingsFill: 0,
    plotDrainageCut: 0, plotDrainageFill: 0,
    wallsCut: 0, wallsFill: 0,
    plotServicesCut: 0, plotServicesFill: 0,
  };
  const [soilManagementData, setSoilManagementData] = useState<SoilManagementData>(emptySoilData);

  // Topsoil table (user-editable, auto-populated from disposal totals)
  const [topsoilOnsite, setTopsoilOnsite] = useState<number>(0);
  const [topsoilOffsite, setTopsoilOffsite] = useState<number>(0);

  // Helper function to get column title with replaced prefix
  const getColumnTitle = React.useCallback(
    (fieldKey: string, defaultTitle: string): string => {
      // Return the constructed title as-is so decimal-mm prefixes from Excel
      // (e.g. "0.150mm") are preserved exactly as imported.
      if (prefixValues[fieldKey] && prefixValues[fieldKey].trim() !== "") {
        return defaultTitle.replace(/xx/g, prefixValues[fieldKey]);
      }
      return defaultTitle;
    },
    [prefixValues]
  );

  // Change detection hook
  /* const {
    hasChanges,
    trackFieldChange,
    trackRowAdded,
    trackRowDeleted,
    resetChanges,
    getChangeSummary,
  } = useChangeDetection<CutFillRow[]>({
    pageId: "cut-fills",
    onChangeDetected: (changes) => {
      console.log("[CutFills] Changes detected:", getChangeSummary());
    },
  }); */

  // Create column mapping for Excel import (Excel title -> data key)
  const columnMapping: { [title: string]: string } = {
    "Plot No": "plotNo",
    "House type": "housetype",
    "Foundation Type": "foundationType",
    "Site strip to remove vegetation": "vegStrip",
    "Excavate to strip topsoil, depth n.e. xxmm": "topsoilStrip",
    "Topsoil Disposal onsite": "topsoilDisposalOnsite",
    "Topsoil Disposal offsite": "topsoilDisposalOffsite",
    "Excavate to reduce level and deposit on site": "cutFounds",
    "Fill to make up levels with site won sub-soil": "fillFounds",
    "Excavate to reduce level and deposit on site": "cutPaths",
    "Fill to make up levels with site won sub-soil": "fillPaths",
    "Excavate to reduce level and deposit on site": "cutGarden",
    "Fill to make up levels with site won sub-soil": "fillGarden",
    "Excavate to reduce level and deposit on site": "cutDrives",
    "Fill to make up levels with site won sub-soil": "fillDrives",
    "Fill to make up levels with 6F2": "fillToMakeUp6F2",
  };

  // Excel import/export hook
  const {
    importLoading,
    importModalVisible,
    setImportModalVisible,
    handleExportExcel,
    handleDownloadTemplate,
    handleImportExcel: baseHandleImportExcel,
  } = useExcelImportExport<CutFillRow>({
    filename: "CutFills",
    sheetName: "Cut & Fills",
    columns: excelColumns,
    data: data,
    onImport: (importedData) => {
      const processedData = importedData
        .map((row, index) => {
          const emptyRow = createEmptyRow(`imported_${Date.now()}_${index}`);

          // Explicitly map the fields to ensure they're not lost
          const merged = {
            ...emptyRow,
            plotNo:
              row.plotNo !== undefined && row.plotNo !== null
                ? safeStringValue(row.plotNo)
                : emptyRow.plotNo,
            housetype:
              row.housetype !== undefined && row.housetype !== null
                ? safeStringValue(row.housetype)
                : emptyRow.housetype,
            foundationType:
              row.foundationType !== undefined && row.foundationType !== null
                ? safeStringValue(row.foundationType)
                : emptyRow.foundationType,
            vegStrip:
              row.vegStrip !== undefined && row.vegStrip !== null
                ? Math.round(Number(row.vegStrip) * 1000) / 1000
                : 0,
            topsoilStrip:
              row.topsoilStrip !== undefined && row.topsoilStrip !== null
                ? Math.round(Number(row.topsoilStrip) * 1000) / 1000
                : 0,
            topsoilDisposalOnsite:
              row.topsoilDisposalOnsite !== undefined &&
              row.topsoilDisposalOnsite !== null
                ? Math.round(Number(row.topsoilDisposalOnsite) * 1000) / 1000
                : 0,
            topsoilDisposalOffsite:
              row.topsoilDisposalOffsite !== undefined &&
              row.topsoilDisposalOffsite !== null
                ? Math.round(Number(row.topsoilDisposalOffsite) * 1000) / 1000
                : 0,
            cutFounds:
              row.cutFounds !== undefined && row.cutFounds !== null
                ? Math.round(Number(row.cutFounds) * 1000) / 1000
                : 0,
            fillFounds:
              row.fillFounds !== undefined && row.fillFounds !== null
                ? Math.round(Number(row.fillFounds) * 1000) / 1000
                : 0,
            cutPaths:
              row.cutPaths !== undefined && row.cutPaths !== null
                ? Math.round(Number(row.cutPaths) * 1000) / 1000
                : 0,
            fillPaths:
              row.fillPaths !== undefined && row.fillPaths !== null
                ? Math.round(Number(row.fillPaths) * 1000) / 1000
                : 0,
            cutGarden:
              row.cutGarden !== undefined && row.cutGarden !== null
                ? Math.round(Number(row.cutGarden) * 1000) / 1000
                : 0,
            fillGarden:
              row.fillGarden !== undefined && row.fillGarden !== null
                ? Math.round(Number(row.fillGarden) * 1000) / 1000
                : 0,
            cutDrives:
              row.cutDrives !== undefined && row.cutDrives !== null
                ? Math.round(Number(row.cutDrives) * 1000) / 1000
                : 0,
            fillDrives:
              row.fillDrives !== undefined && row.fillDrives !== null
                ? Math.round(Number(row.fillDrives) * 1000) / 1000
                : 0,
            fillToMakeUp6F2:
              row.fillToMakeUp6F2 !== undefined && row.fillToMakeUp6F2 !== null
                ? Math.round(Number(row.fillToMakeUp6F2) * 1000) / 1000
                : 0,
          };

          return merged;
        })
        .filter((row, index) => {
          // Skip rows where ALL key fields are empty or zero
          const plotNoStr = String(row.plotNo || "").trim();
          const housetypeStr = String(row.housetype || "").trim();
          const foundationTypeStr = String(row.foundationType || "").trim();

          // Check if row has any meaningful data
          // A row is valid if it has ANY non-empty, non-zero value in text fields
          const hasAnyTextData =
            (plotNoStr && plotNoStr !== "0") ||
            (housetypeStr && housetypeStr !== "0") ||
            (foundationTypeStr && foundationTypeStr !== "0");

          // Check if row has any non-zero numeric data
          const hasAnyNumericData =
            row.vegStrip > 0 ||
            row.topsoilStrip > 0 ||
            row.topsoilDisposalOnsite > 0 ||
            row.topsoilDisposalOffsite > 0 ||
            row.cutFounds > 0 ||
            row.fillFounds > 0 ||
            row.cutPaths > 0 ||
            row.fillPaths > 0 ||
            row.cutGarden > 0 ||
            row.fillGarden > 0 ||
            row.cutDrives > 0 ||
            row.fillDrives > 0 ||
            row.fillToMakeUp6F2 > 0;

          const isValid = hasAnyTextData || hasAnyNumericData;

          return isValid;
        });

      setData(processedData);
      updateField("groundworks", {
        ...tender.groundworks,
        cutFillsTable: processedData,
      });
    },
    skipValidation: true,
  });

  // Custom import handler with direct Excel processing
  const handleImportExcel = async (file: File) => {
    setImportModalVisible(false);

    try {
      const XLSX = await import("xlsx");
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: "array" });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];

          // Convert to JSON
          const jsonData = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            raw: true,
            blankrows: false,
          }) as any[][];

          // Find header row - look for "Plot No"
          let headerRowIndex = -1;
          for (let i = 0; i < jsonData.length; i++) {
            const row = jsonData[i];
            if (
              row.some(
                (cell) =>
                  String(cell).includes("Plot") && String(cell).includes("No"),
              )
            ) {
              headerRowIndex = i;
              break;
            }
          }

          if (headerRowIndex === -1) {
            alert("Could not find header row with 'Plot No'");
            return;
          }

          const headers = jsonData[headerRowIndex];

          // Extract prefix values from headers (dimension before base text)
          const extractedPrefixValues: Record<string, string> = {};
          const prefixFields = [
            {
              fieldKey: "topsoilStrip",
              baseText: "Excavate to strip topsoil, depth n.e.",
            },
          ];

          // Search header row and subsequent sub-header rows (up to 4 rows below)
          // Excel has multi-row headers: Row 3 = "Plot No", Row 4 = group headers, Row 5 = detailed column titles
          const headerRowsToSearch: any[][] = [];
          for (let r = 0; r <= 4 && headerRowIndex + r < jsonData.length; r++) {
            if (jsonData[headerRowIndex + r]) {
              headerRowsToSearch.push(jsonData[headerRowIndex + r]);
            }
          }

          prefixFields.forEach(({ fieldKey, baseText }) => {
            let found = false;
            for (const headerRow of headerRowsToSearch) {
              if (found) break;
              for (
                let columnIndex = 0;
                columnIndex < headerRow.length;
                columnIndex++
              ) {
                const headerValue = String(
                  headerRow[columnIndex] || ""
                ).trim();

                if (headerValue.toLowerCase().includes(baseText.toLowerCase())) {
                  const escapedBaseText = baseText.replace(
                    /[.*+?^${}()|[\]\\]/g,
                    "\\$&"
                  );
                  const suffixMatch = headerValue.match(
                    new RegExp(`${escapedBaseText}\\s*(.+)`, "i")
                  );

                  if (suffixMatch && suffixMatch[1]) {
                    let extractedValue = suffixMatch[1].trim();
                    extractedValue = extractedValue
                      .replace(/\s*\(.*?\)\s*/g, "")
                      .replace(/\s*(m[²³]|m2|m3)\s*$/i, "")
                      .trim();
                    const numericMatch = extractedValue.match(/^(\d+(?:\.\d+)?)/);
                    if (numericMatch) {
                      extractedPrefixValues[fieldKey] = numericMatch[1];
                    }
                  }
                  found = true;
                  break;
                }
              }
            }
          });

          // Set the extracted prefix values to state
          if (Object.keys(extractedPrefixValues).length > 0) {
            setPrefixValues(extractedPrefixValues);
          }

          // Get all rows after header
          const allDataRows = jsonData.slice(headerRowIndex + 1);

          // Find where actual data starts by looking for the first row with a valid house type
          let dataStartIndex = 0;
          let foundDataStart = false;

          for (let i = 0; i < allDataRows.length; i++) {
            const row = allDataRows[i];
            if (row && row.length > 0) {
              const plotNoValue = String(row[0] || "").trim();
              const houseTypeValue = String(row[1] || "").trim();

              // Look for first row that has both plot number and house type that look like actual data
              // House types should be text (not empty, not "total", not purely numeric)
              const hasValidPlotNo =
                plotNoValue &&
                plotNoValue !== "" &&
                !plotNoValue.toLowerCase().includes("total");
              const hasValidHouseType =
                houseTypeValue &&
                houseTypeValue !== "" &&
                !houseTypeValue.toLowerCase().includes("total") &&
                !houseTypeValue.toLowerCase().includes("sum") &&
                !houseTypeValue.toLowerCase().includes("note") &&
                isNaN(Number(houseTypeValue)) && // Not purely numeric
                houseTypeValue.match(/[a-zA-Z]/); // Contains letters

              if (hasValidPlotNo && hasValidHouseType) {
                dataStartIndex = i;
                foundDataStart = true;
                break;
              }
            }
          }

          const dataRows = allDataRows.slice(dataStartIndex);

          // Map to CutFillRow objects. Detect "GARAGES" separator row to tag
          // rows as plot or garage — same pattern as `processFoundationsData`
          // / `processCutFillsData` in `JobTypeSelection.tsx`.
          let isGarageSection = false;
          const allImportedRows: CutFillRow[] = [];
          const nonEmptyDataRows = dataRows.filter(
            (row) =>
              row &&
              row.length > 0 &&
              row.some(
                (cell) => cell !== null && cell !== undefined && cell !== "",
              ),
          );
          nonEmptyDataRows.forEach((row, index) => {
            const firstCell = String(row[0] || "").trim().toUpperCase();
            if (firstCell === "GARAGES" || firstCell === "GARAGE") {
              isGarageSection = true;
              return; // Skip the separator row
            }
            const mapped: CutFillRow = {
              key: `imported_${Date.now()}_${index}`,
              plotNo: String(row[0] || ""),
              housetype: String(row[1] || ""),
              foundationType: String(row[2] || ""),
              plotType: isGarageSection ? "garage" : "plot",
              vegStrip: Math.round((Number(row[3]) || 0) * 1000) / 1000,
              topsoilStrip: Math.round((Number(row[4]) || 0) * 1000) / 1000,
              topsoilDisposalOnsite:
                Math.round((Number(row[5]) || 0) * 1000) / 1000,
              topsoilDisposalOffsite:
                Math.round((Number(row[6]) || 0) * 1000) / 1000,
              cutFounds: Math.round((Number(row[7]) || 0) * 1000) / 1000,
              fillFounds: Math.round((Number(row[8]) || 0) * 1000) / 1000,
              cutPaths: Math.round((Number(row[9]) || 0) * 1000) / 1000,
              fillPaths: Math.round((Number(row[10]) || 0) * 1000) / 1000,
              cutGarden: Math.round((Number(row[11]) || 0) * 1000) / 1000,
              fillGarden: Math.round((Number(row[12]) || 0) * 1000) / 1000,
              cutDrives: Math.round((Number(row[13]) || 0) * 1000) / 1000,
              fillDrives: Math.round((Number(row[14]) || 0) * 1000) / 1000,
              fillToMakeUp6F2: Math.round((Number(row[15]) || 0) * 1000) / 1000,
            };
            if (
              mapped.plotNo ||
              mapped.vegStrip > 0 ||
              mapped.topsoilStrip > 0 ||
              mapped.cutFounds > 0 ||
              mapped.fillFounds > 0
            ) {
              allImportedRows.push(mapped);
            }
          });

          // Filter out invalid rows but keep all actual plot data
          const importedRows = allImportedRows.filter((row) => {
            const plotNoStr = String(row.plotNo || "").trim();
            const housetypeStr = String(row.housetype || "").trim();

            // Keep rows that have valid plot numbers and house types
            const hasValidPlotNo =
              plotNoStr !== "" &&
              !plotNoStr.toLowerCase().includes("total") &&
              !plotNoStr.toLowerCase().includes("sum");
            const hasValidHouseType =
              housetypeStr !== "" &&
              !housetypeStr.toLowerCase().includes("total") &&
              !housetypeStr.toLowerCase().includes("sum") &&
              isNaN(Number(housetypeStr)) && // Not purely numeric
              housetypeStr.match(/[a-zA-Z]/); // Contains letters

            const isValidDataRow = hasValidPlotNo && hasValidHouseType;

            return isValidDataRow;
          });

          if (importedRows.length === 0) {
            alert("No valid data found to import");
            return;
          }

          setData(importedRows);
          updateField("groundworks", {
            ...tender.groundworks,
            cutFillsTable: importedRows,
            ...(Object.keys(extractedPrefixValues).length > 0 && {
              cutFillsPrefixValues: extractedPrefixValues,
            }),
          });
          alert(`Successfully imported ${importedRows.length} rows`);
        } catch (error) {
          console.error("Import error:", error);
          alert(`Import failed: ${error.message}`);
        }
      };

      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error("Import error:", error);
      alert(`Import failed: ${error.message}`);
    }
  };

  useEffect(() => {
    if (tender?.groundworks?.cutFillsTable) {
      setData(tender.groundworks.cutFillsTable);
    } else {
      // Initialize with empty row
      setData([createEmptyRow()]);
    }
  }, [tender?.groundworks?.cutFillsTable]);

  // Compute Subsoil auto-rows (Foundations C&F, Slab Paths, Gardens, Drives, Foundations
  // Arisings, Plot Drainage, Walls, Plot Services) via the SAME shared
  // computeSoilManagementDerivedCutFillValues() (groundworksBoqSummaryComputer.ts) that
  // GroundworksCutFillBillSummary.tsx's live Qty column and the persisted SummaryKey
  // payload both call — single source of truth for this formula across all three
  // places, no state update, no updateField, no cascade. `cutFillsTable` is overridden
  // with this page's own live-editing `data` state (not yet flushed to
  // `tender.groundworks.cutFillsTable`) since rows 1–4 need the value as typed, not the
  // last-saved snapshot. The other 4 rows' FILL values remain in `soilManagementData`
  // (populated via takeoff-sheet import) — no live source for those.
  const effectiveSoilManagementData = React.useMemo<SoilManagementData>(() => {
    return {
      ...soilManagementData,
      ...computeSoilManagementDerivedCutFillValues(
        { ...tender?.groundworks, cutFillsTable: data },
        retWallsBuildUp?.specifications,
      ),
    };
  }, [soilManagementData, data, tender?.groundworks, retWallsBuildUp]);

  // Formula inspector modal state for the 4 auto-computed Subsoil rows
  const [subsoilFormulaModal, setSubsoilFormulaModal] = React.useState<{
    visible: boolean;
    label: string;
    cutField: keyof SoilManagementData;
    fillField: keyof SoilManagementData;
    rowKey: 'cutFounds' | 'cutPaths' | 'cutGarden' | 'cutDrives';
    fillRowKey: 'fillFounds' | 'fillPaths' | 'fillGarden' | 'fillDrives';
  } | null>(null);
  const [subsoilDetailModal, setSubsoilDetailModal] = React.useState<'foundationsArisings' | 'plotDrainage' | 'walls' | 'plotServices' | null>(null);

  // Load prefix values from tender state
  useEffect(() => {
    const contextPrefixValues = tender?.groundworks?.cutFillsPrefixValues;
    if (contextPrefixValues && Object.keys(contextPrefixValues).length > 0) {
      const isDifferent =
        JSON.stringify(contextPrefixValues) !== JSON.stringify(prefixValues);
      if (isDifferent) {
        setPrefixValues(contextPrefixValues);
      }
    }
  }, [tender.groundworks]);

  // Save prefix values to tender state when they change
  useEffect(() => {
    if (Object.keys(prefixValues).length > 0) {
      updateField("groundworks", {
        ...tender.groundworks,
        cutFillsPrefixValues: prefixValues,
      });
      setTimeout(() => {
        saveTender();
      }, 100);
    }
  }, [prefixValues]);

  // Reset temp values when modal opens
  useEffect(() => {
    if (configModalVisible && !prevConfigModalVisibleRef.current) {
      setTempPrefixValues({ ...prefixValues });
    }
    prevConfigModalVisibleRef.current = configModalVisible;
  }, [configModalVisible, prefixValues]);

  // Reset soil modal temp values when it opens
  useEffect(() => {
    if (soilConfigModalVisible) {
      setTempTopsoilManagement(topsoilManagement);
      setTempTopsoilSubOption(topsoilSubOption);
      setTempSubsoilManagement(subsoilManagement);
      setTempSubsoilSubOption(subsoilSubOption);
      setTempSubsoilDisposalClassification(subsoilDisposalClassification);
    }
  }, [soilConfigModalVisible]);

  // Load topsoil & subsoil management values from tender state
  useEffect(() => {
    const tm = tender?.groundworks?.topsoilManagement;
    if (tm) {
      setTopsoilManagement(tm.management || "");
      setTopsoilSubOption(tm.subOption || "");
    }
    const sm = tender?.groundworks?.subsoilManagement;
    if (sm) {
      setSubsoilManagement(sm.management || "");
      setSubsoilSubOption(sm.subOption || "");
      setSubsoilDisposalClassification(sm.disposalClassification || "");
    }
    const sd = tender?.groundworks?.soilManagementData;
    if (sd) {
      setSoilManagementData({
        foundationsCutArisings: sd.foundationsCutArisings ?? 0,
        foundationsFill: sd.foundationsFill ?? 0,
        slabPathsCut: sd.slabPathsCut ?? 0,
        slabPathsFill: sd.slabPathsFill ?? 0,
        gardensCut: sd.gardensCut ?? 0,
        gardensFill: sd.gardensFill ?? 0,
        drivesCut: sd.drivesCut ?? 0,
        drivesFill: sd.drivesFill ?? 0,
        foundationsArisingsCut: sd.foundationsArisingsCut ?? 0,
        foundationsArisingsFill: sd.foundationsArisingsFill ?? 0,
        plotDrainageCut: sd.plotDrainageCut ?? 0,
        plotDrainageFill: sd.plotDrainageFill ?? 0,
        wallsCut: sd.wallsCut ?? 0,
        wallsFill: sd.wallsFill ?? 0,
        plotServicesCut: sd.plotServicesCut ?? 0,
        plotServicesFill: sd.plotServicesFill ?? 0,
      });
    }
    // Topsoil table: always derive from live cutFillsTable disposal column totals
    const table = tender?.groundworks?.cutFillsTable || [];
    const onsite = table.reduce((s: number, r: any) => s + (Number(r.topsoilDisposalOnsite) || 0), 0);
    const offsite = table.reduce((s: number, r: any) => s + (Number(r.topsoilDisposalOffsite) || 0), 0);
    setTopsoilOnsite(Math.round(onsite * 1000) / 1000);
    setTopsoilOffsite(Math.round(offsite * 1000) / 1000);
  }, [tender?.groundworks?.topsoilManagement, tender?.groundworks?.subsoilManagement, tender?.groundworks?.soilManagementData, tender?.groundworks?.cutFillsTable]);

  // Auto-derive Foundations Arisings (Cut) from foundations table
  // [SUPERSEDED – takeoff sheet import is the sole writer to soilManagementData.
  // foundationsArisingsCut no longer auto-computes from foundationsTable; it stays
  // at whatever the import (or persisted tender state) sets, defaulting to 0.]
  /*
  const prevDerivedFoundationsArisingsCutRef = useRef<number | null>(null);
  useEffect(() => {
    const foundationsTable: FoundationRow[] = tender?.groundworks?.foundationsTable || [];
    const derived = Math.round(
      foundationsTable.reduce((sum, row) =>
        sum + (Number(row.stockpileArisings) || 0) + (Number(row.depositOnSite) || 0), 0
      ) * 1000
    ) / 1000;

    if (prevDerivedFoundationsArisingsCutRef.current === derived) return;
    prevDerivedFoundationsArisingsCutRef.current = derived;

    setSoilManagementData(prev => ({ ...prev, foundationsArisingsCut: derived }));

    const currentSaved = Math.round((tender?.groundworks?.soilManagementData?.foundationsArisingsCut ?? 0) * 1000) / 1000;
    if (currentSaved !== derived) {
      updateField("groundworks", {
        ...tender.groundworks,
        soilManagementData: {
          ...tender?.groundworks?.soilManagementData,
          foundationsArisingsCut: derived,
        },
      });
    }
  }, [tender?.groundworks?.foundationsTable]);
  */

  // Auto-update topsoil management type from topsoil table balance (live)
  useEffect(() => {
    const balance = Math.round((topsoilOnsite - topsoilOffsite) * 1000) / 1000;
    const newManagement = balance > 0 ? "Surplus" : balance < 0 ? "Deficit" : "";
    // Read the persisted sub-option directly from tender state rather than local
    // component state, which can still hold its stale initial value the first
    // time this effect fires (before the "load from tender" effect has flushed).
    const persistedTopsoil = tender?.groundworks?.topsoilManagement;
    const currentManagement = persistedTopsoil?.management || "";
    // Only update if the management type needs to change
    if (newManagement === currentManagement) return;
    setTopsoilManagement(newManagement);
    const currentSubOption = persistedTopsoil?.subOption || "";
    const newCode = newManagement ? resolveTopsoilCodePure(newManagement, currentSubOption) : "";
    updateField("groundworks", {
      ...tender.groundworks,
      topsoilTableData: { onsite: topsoilOnsite, offsite: topsoilOffsite },
      topsoilManagement: {
        management: newManagement,
        subOption: currentSubOption,
        code: newCode,
      },
    });
  }, [topsoilOnsite, topsoilOffsite, tender?.groundworks?.topsoilManagement]);

  // Auto-update subsoil management type from subsoil table balance (live)
  // Uses effectiveSoilManagementData so the 4 auto-computed rows (cutFillsTable-derived)
  // are always fresh without requiring a state-update cascade.
  useEffect(() => {
    const subsoilTotalCut =
      (effectiveSoilManagementData.foundationsCutArisings || 0) +
      (effectiveSoilManagementData.slabPathsCut || 0) +
      (effectiveSoilManagementData.gardensCut || 0) +
      (effectiveSoilManagementData.drivesCut || 0) +
      (effectiveSoilManagementData.foundationsArisingsCut || 0) +
      (effectiveSoilManagementData.plotDrainageCut || 0) +
      (effectiveSoilManagementData.wallsCut || 0) +
      (effectiveSoilManagementData.plotServicesCut || 0);
    const subsoilTotalFill =
      (effectiveSoilManagementData.foundationsFill || 0) +
      (effectiveSoilManagementData.slabPathsFill || 0) +
      (effectiveSoilManagementData.gardensFill || 0) +
      (effectiveSoilManagementData.drivesFill || 0) +
      (effectiveSoilManagementData.foundationsArisingsFill || 0) +
      (effectiveSoilManagementData.plotDrainageFill || 0) +
      (effectiveSoilManagementData.wallsFill || 0) +
      (effectiveSoilManagementData.plotServicesFill || 0);
    const balance = Math.round((subsoilTotalCut - subsoilTotalFill) * 1000) / 1000;
    const newManagement = balance > 0 ? "Surplus" : balance < 0 ? "Deficit" : "";
    // Read the persisted sub-option/classification directly from tender state — see
    // the topsoil effect above for why local component state can't be trusted here.
    const persistedSubsoil = tender?.groundworks?.subsoilManagement;
    const currentManagement = persistedSubsoil?.management || "";
    if (newManagement === currentManagement) return;
    setSubsoilManagement(newManagement);
    const currentSubOption = persistedSubsoil?.subOption || "";
    const currentDisposalClassification = persistedSubsoil?.disposalClassification || "";
    const newCode = newManagement
      ? resolveSubsoilCodePure(newManagement, currentSubOption, currentDisposalClassification)
      : "";
    updateField("groundworks", {
      ...tender.groundworks,
      soilManagementData: { ...effectiveSoilManagementData },
      subsoilManagement: {
        management: newManagement,
        subOption: currentSubOption,
        disposalClassification: currentDisposalClassification,
        code: newCode,
      },
    });
  }, [effectiveSoilManagementData, tender?.groundworks?.subsoilManagement]);

  // Topsoil Management code resolver (pure function, no side effects)
  const resolveTopsoilCodePure = (management: string, subOption: string): string => {
    if (management === "Deficit") {
      if (subOption === "Import clean topsoil") return "HAUL0002";
      if (subOption === "Excluded") return "None";
    }
    if (management === "Surplus") {
      if (subOption === "Stockpile on site for re-use" || subOption === "Topsoil Disposal onsite") return "GWALP035";
      if (subOption === "Removal surplus topsoil arisings from site") return "AMAS005";
    }
    return "";
  };

  // Subsoil Management code resolver (pure function, no side effects)
  const resolveSubsoilCodePure = (management: string, subOption: string, disposalClassification: string): string => {
    if (management === "Deficit") {
      if (subOption === "Import clean subsoil") return "HAUL0001";
      if (subOption === "Excluded") return "None";
    }
    if (management === "Surplus") {
      if (subOption === "Topsoil Disposal onsite") return "GWALP035";
      if (subOption === "Remove surplus subsoil arisings offsite") {
        if (disposalClassification === "Inert") return "AMAS004";
        if (disposalClassification === "Non Haz") return "AMAS010";
        if (disposalClassification === "Haz") return "AMAS012";
        if (disposalClassification === "Asbestos") return "AMAS011";
      }
    }
    return "";
  };

  // Topsoil Management code resolver
  const resolveTopsoilCode = (management: string, subOption: string): string => {
    if (management === "Deficit") {
      if (subOption === "Import clean topsoil") return "HAUL0002";
      if (subOption === "Excluded") return "None";
    }
    if (management === "Surplus") {
      if (subOption === "Stockpile on site for re-use" || subOption === "Topsoil Disposal onsite") return "GWALP035";
      if (subOption === "Removal surplus topsoil arisings from site") return "AMAS005";
    }
    return "";
  };

  // Subsoil Management code resolver
  const resolveSubsoilCode = (management: string, subOption: string, disposalClassification: string): string => {
    if (management === "Deficit") {
      if (subOption === "Import clean subsoil") return "HAUL0001";
      if (subOption === "Excluded") return "None";
    }
    if (management === "Surplus") {
      if (subOption === "Topsoil Disposal onsite") return "GWALP035";
      if (subOption === "Remove surplus subsoil arisings offsite") {
        if (disposalClassification === "Inert") return "AMAS004";
        if (disposalClassification === "Non Haz") return "AMAS010";
        if (disposalClassification === "Haz") return "AMAS012";
        if (disposalClassification === "Asbestos") return "AMAS011";
      }
    }
    return "";
  };

  // Handler: apply Site Clearance Configure Headers (prefix values only)
  const handleSiteClearanceConfigApply = () => {
    setPrefixValues(tempPrefixValues);
    updateField("groundworks", {
      ...tender.groundworks,
      cutFillsPrefixValues: tempPrefixValues,
    });
    setTimeout(() => saveTender(), 100);
  };

  // Handler: apply Soil Management Configure Headers
  const handleSubsoilConfigApply = () => {
    const resolvedTopsoilCode = resolveTopsoilCode(tempTopsoilManagement, tempTopsoilSubOption);
    const resolvedSubsoilCode = resolveSubsoilCode(tempSubsoilManagement, tempSubsoilSubOption, tempSubsoilDisposalClassification);

    setTopsoilManagement(tempTopsoilManagement);
    setTopsoilSubOption(tempTopsoilSubOption);
    setSubsoilManagement(tempSubsoilManagement);
    setSubsoilSubOption(tempSubsoilSubOption);
    setSubsoilDisposalClassification(tempSubsoilDisposalClassification);
    updateField("groundworks", {
      ...tender.groundworks,
      topsoilManagement: {
        management: tempTopsoilManagement,
        subOption: tempTopsoilSubOption,
        code: resolvedTopsoilCode,
      },
      subsoilManagement: {
        management: tempSubsoilManagement,
        subOption: tempSubsoilSubOption,
        disposalClassification: tempSubsoilDisposalClassification,
        code: resolvedSubsoilCode,
      },
    });
    setTimeout(() => saveTender(), 100);
  };

  const createEmptyRow = (key?: string, plotType: "plot" | "garage" = "plot"): CutFillRow => ({
    key: key || Date.now().toString(),
    plotNo: "",
    housetype: "",
    foundationType: "",
    plotType,
    vegStrip: 0,
    topsoilStrip: 0,
    topsoilDisposalOnsite: 0,
    topsoilDisposalOffsite: 0,
    cutFounds: 0,
    fillFounds: 0,
    cutPaths: 0,
    fillPaths: 0,
    cutGarden: 0,
    fillGarden: 0,
    cutDrives: 0,
    fillDrives: 0,
    fillToMakeUp6F2: 0,
  });

  const safeStringValue = (value: any): string => {
    if (value === null || value === undefined) return "";
    return String(value);
  };

  // Mirrors the groundworksRef pattern already used in PlotDrives.tsx: keeps a
  // ref to the latest tender.groundworks so handleInputChange can read it
  // without closing over `tender` directly, which lets handleInputChange (and
  // the column definitions that reference it) stay referentially stable
  // across renders for useMemo below, instead of rebuilding on every render.
  const groundworksRef = useRef(tender?.groundworks);
  useEffect(() => {
    groundworksRef.current = tender?.groundworks;
  }, [tender?.groundworks]);

  // Same reasoning as groundworksRef above: baseColumns (below) is memoized
  // on [handleInputChange] only, so a Plot No duplicate check inside its
  // render closure must read rows via a ref, not the `data` state directly
  // (which the closure would otherwise capture stale).
  const dataRef = useRef(data);
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const handleInputChange = useCallback(
    (key: string, field: keyof CutFillRow, value: string | number) => {
      setData((prevData) => {
        const newData = prevData.map((row) => {
          if (row.key === key) {
            return { ...row, [field]: value };
          }
          return row;
        });

        // Update tender context
        updateField("groundworks", {
          ...groundworksRef.current,
          cutFillsTable: newData,
        });
        return newData;
      });
    },
    [updateField],
  );

  const addRow = () => {
    const newRow = createEmptyRow(
      undefined,
      plotTypeFilter === "garages" ? "garage" : "plot",
    );
    // trackRowAdded(newRow.key);
    const newData = [...data, newRow];
    setData(newData);
    updateField("groundworks", {
      ...tender.groundworks,
      cutFillsTable: newData,
    });
  };

  const deleteRow = useCallback((key: string) => {
    setKeyToDelete(key);
    setKeysToDelete([]);
    setDeleteModalVisible(true);
  }, []);

  const deleteSelectedRows = () => {
    setKeysToDelete(selectedCutFillKeys as string[]);
    setKeyToDelete(null);
    setDeleteModalVisible(true);
  };

  const confirmDelete = () => {
    const keysBeingDeleted = keysToDelete.length > 0 ? keysToDelete : keyToDelete ? [keyToDelete] : [];
    if (keysBeingDeleted.length > 0) {
      // trackRowDeleted(keyToDelete);
      const keySet = new Set(keysBeingDeleted);
      const newData = data.filter((row) => !keySet.has(row.key));
      setData(newData);
      // Deleting the last remaining row leaves nothing for the ad-hoc extra
      // items (Cut & Fill Bill Summary's "Add Section"/"Add Item" rows) to be
      // relevant to — clear cutFillsExtraItems along with the table itself so
      // a genuinely-emptied take-off doesn't leave orphaned extra rows behind
      // (see GroundworksCutFillBillSummary.tsx/GroundworksReview.tsx, which
      // otherwise keep showing them independently of the take-off table).
      updateField("groundworks", {
        ...tender.groundworks,
        cutFillsTable: newData,
        ...(newData.length === 0 && { cutFillsExtraItems: {} }),
      });
      setSelectedCutFillKeys([]);
    }
    setDeleteModalVisible(false);
    setKeyToDelete(null);
    setKeysToDelete([]);
  };

  const cancelDelete = () => {
    setDeleteModalVisible(false);
    setKeyToDelete(null);
    setKeysToDelete([]);
  };

  // Filter rows by the View dropdown (plots / garages). Rows imported before
  // `plotType` existed default to "plot" via the `!row.plotType` fallback —
  // same migration pattern as `FoundationsNew.tsx`. Memoized so this doesn't
  // re-filter the full table on every unrelated render (e.g. keystrokes in
  // other tabs' inputs) — only when the underlying rows or the view toggle
  // actually change.
  const filteredData = React.useMemo(
    () =>
      data.filter((row) =>
        plotTypeFilter === "plots"
          ? row.plotType === "plot" || !row.plotType
          : row.plotType === "garage",
      ),
    [data, plotTypeFilter],
  );

  // Calculate totals for the currently-visible view (plots OR garages),
  // matching the rows the table renders below the summary. Memoized on
  // filteredData so the reduce only re-runs when the visible rows change,
  // not on every render (e.g. modal state toggles).
  const totals = React.useMemo(() => {
    const totals = {
      vegStrip: 0,
      topsoilStrip: 0,
      topsoilDisposalOnsite: 0,
      topsoilDisposalOffsite: 0,
      cutFounds: 0,
      fillFounds: 0,
      cutPaths: 0,
      fillPaths: 0,
      cutGarden: 0,
      fillGarden: 0,
      cutDrives: 0,
      fillDrives: 0,
      fillToMakeUp6F2: 0,
    };

    filteredData.forEach((row) => {
      totals.vegStrip += Number(row.vegStrip) || 0;
      totals.topsoilStrip += Number(row.topsoilStrip) || 0;
      totals.topsoilDisposalOnsite += Number(row.topsoilDisposalOnsite) || 0;
      totals.topsoilDisposalOffsite += Number(row.topsoilDisposalOffsite) || 0;
      totals.cutFounds += Number(row.cutFounds) || 0;
      totals.fillFounds += Number(row.fillFounds) || 0;
      totals.cutPaths += Number(row.cutPaths) || 0;
      totals.fillPaths += Number(row.fillPaths) || 0;
      totals.cutGarden += Number(row.cutGarden) || 0;
      totals.fillGarden += Number(row.fillGarden) || 0;
      totals.cutDrives += Number(row.cutDrives) || 0;
      totals.fillDrives += Number(row.fillDrives) || 0;
      totals.fillToMakeUp6F2 += Number(row.fillToMakeUp6F2) || 0;
    });

    return totals;
  }, [filteredData]);

  const getTabDisplayName = (tabKey: string) => {
    const tabNames = {
      "site-clearance": "Site Clearance",
      "cut-fill": "Cut & Fill",
    };
    return tabNames[tabKey as keyof typeof tabNames] || "Cut & Fills";
  };

  const getScrollConfig = (columns: any[]) => {
    const totalWidth = columns.reduce((sum: number, col: any) => {
      if (col.children) {
        return sum + col.children.reduce((s: number, child: any) => s + (child.width || 120), 0);
      }
      return sum + (col.width || 120);
    }, 0);
    const estimatedRowHeight = 50;
    const maxComfortableRows = 12;
    const needsVerticalScroll = data.length > maxComfortableRows;
    return { x: totalWidth, y: needsVerticalScroll ? maxComfortableRows * estimatedRowHeight : undefined };
  };

  const handleSave = () => {
    saveTender();
    // Note: Don't reset changes here - changes persist until "Send for Review"
  };

  const handleSaveAndContinue = async () => {
    try {
      message.loading({ content: "Saving tender...", key: "saveTender", duration: 0 });

      // Update tender state with hook values
      updateField("groundworks", {
        ...tender.groundworks,
        topsoilTableData: { onsite: topsoilOnsite, offsite: topsoilOffsite },
        soilManagementData: { ...soilManagementData },
        adjustments: {
          preliminaries: groundworksTotals.preliminariesAmount,
          ohp: groundworksTotals.ohpAmount,
          discount: groundworksTotals.discountAmount,
          includePreliminaries: groundworksTotals.preliminariesPercent > 0,
          includeOhp: groundworksTotals.ohpPercent > 0,
          includeDiscount: groundworksTotals.discountPercent > 0,
        },
        totals: {
          ...tender.groundworks?.totals,
          grandTotal: groundworksTotals.grandTotal,
          totalWithAdjustments: groundworksTotals.tenderTotal,
          tenderTotal: groundworksTotals.tenderTotal, // FINAL total
        },
      });

      // Update global settings with percentages
      updateField("globalSettings", {
        ...tender.globalSettings,
        groundworks: {
          overheadAndProfit: groundworksTotals.ohpPercent,
          preliminaries: groundworksTotals.preliminariesPercent,
          discount: groundworksTotals.discountPercent,
        },
      });

      updateField("status", "In-progress");
      updateField("lastModified", new Date());

      // Save to localStorage
      await saveTender();
      await new Promise((resolve) => setTimeout(resolve, 200));

      // Call both BOQ and BOQ-versions endpoints
      await forceSaveBothBoqEndpoints(
        tender,
        "Save and continue from Cut-Fills",
        {
          grandTotal: groundworksTotals.grandTotal,
          grandTotalWithAdjustments:
            groundworksTotals.grandTotalWithAdjustments,
          preliminariesAmount: groundworksTotals.preliminariesAmount,
          ohpAmount: groundworksTotals.ohpAmount,
          discountAmount: groundworksTotals.discountAmount,
          tenderTotal: groundworksTotals.tenderTotal,
          preliminariesPercent: groundworksTotals.preliminariesPercent,
          ohpPercent: groundworksTotals.ohpPercent,
          discountPercent: groundworksTotals.discountPercent,
        },
        user?.name,
        itemLibraryData,
      );

      // Note: Don't reset changes here - changes persist until "Send for Review"
      message.destroy("saveTender");
      setTimeout(() => navigate("/groundworks/foundations"), 500);
    } catch (error: any) {
      console.error("❌ Error submitting tender:", error);
      message.error({
        content: `Failed to submit tender: ${
          error.response?.data?.message || error.message || "Unknown error"
        }`,
        key: "saveTender",
        duration: 5,
      });
    }
  };

  // Memoized so this array (and its per-render Input closures) is only
  // rebuilt when handleInputChange actually changes identity, instead of on
  // every keystroke/render — handleInputChange is itself stabilized via
  // useCallback above, so in practice this now only rebuilds once.
  const baseColumns = React.useMemo(
    () => [
    {
      title: "Plot No",
      dataIndex: "plotNo",
      key: "plotNo",
      width: 130,
      fixed: "left" as const,
      render: (text: string | number, record: CutFillRow) => {
        const prefix = record.plotType === "garage" ? "G-" : "P-";
        const raw = text != null ? String(text) : "";
        const suffix = raw.startsWith(prefix) ? raw.slice(prefix.length) : raw;
        return (
          <BufferedTextInput
            addonBefore={prefix}
            value={suffix}
            onCommit={(newSuffix) => {
              const newPlotNo = prefix + newSuffix;
              if (isDuplicatePlotNo(dataRef.current, record.key, newPlotNo)) {
                message.error(`Plot No "${newPlotNo}" is already in use.`);
                return;
              }
              handleInputChange(record.key, "plotNo", newPlotNo);
            }}
            placeholder="1"
            style={{ minWidth: 80 }}
          />
        );
      },
    },
    {
      title: "Housetype",
      dataIndex: "housetype",
      key: "housetype",
      width: 110,
      fixed: "left" as const,
      render: (text: string | number, record: CutFillRow) => (
        <BufferedTextInput
          value={text != null ? String(text) : ""}
          onCommit={(newValue) =>
            handleInputChange(record.key, "housetype", newValue)
          }
          placeholder="Housetype"
        />
      ),
    },
    {
      title: "Foundation Type",
      dataIndex: "foundationType",
      key: "foundationType",
      width: 120,
      fixed: "left" as const,
      render: (text: string | number, record: CutFillRow) => (
        <BufferedTextInput
          value={text != null ? String(text) : ""}
          onCommit={(newValue) =>
            handleInputChange(record.key, "foundationType", newValue)
          }
          placeholder="Foundation Type"
        />
      ),
    },
    ],
    [handleInputChange],
  );

  // Memoized: rebuilds only when its actual inputs change, rather than on
  // every render (e.g. typing in an unrelated cell, tab-unrelated state
  // updates), avoiding a full column-array + addColumnCodes recompute each time.
  const siteClearanceColumns = React.useMemo(() => [
    ...baseColumns,
    {
      title: formatColumnTitle("Site strip to remove vegetation m²"),
      dataIndex: "vegStrip",
      key: "vegStrip",
      width: getRecommendedColumnWidth("vegStrip"),
      render: (value: number, record: CutFillRow) => (
        <NumberInput
          value={value}
          onChange={(newValue) =>
            handleInputChange(record.key, "vegStrip", newValue)
          }
          decimalPlaces={3}
          placeholder="100"
        />
      ),
    },
    {
      title: formatColumnTitle(
        getColumnTitle("topsoilStrip", "Excavate to strip topsoil, depth n.e. xxmm m³"),
      ),
      dataIndex: "topsoilStrip",
      key: "topsoilStrip",
      width: getRecommendedColumnWidth("topsoilStrip"),
      render: (value: number, record: CutFillRow) => (
        <NumberInput
          value={value}
          onChange={(newValue) =>
            handleInputChange(record.key, "topsoilStrip", newValue)
          }
          decimalPlaces={3}
          placeholder="100"
        />
      ),
    },
    {
      title: formatColumnTitle("Topsoil Disposal onsite m³"),
      dataIndex: "topsoilDisposalOnsite",
      key: "topsoilDisposalOnsite",
      width: 180,
      render: (value: number, record: CutFillRow) => (
        <NumberInput
          value={value}
          onChange={(newValue) =>
            handleInputChange(record.key, "topsoilDisposalOnsite", newValue)
          }
          decimalPlaces={3}
          placeholder="100"
        />
      ),
    },
    {
      title: formatColumnTitle("Topsoil Disposal offsite m³"),
      dataIndex: "topsoilDisposalOffsite",
      key: "topsoilDisposalOffsite",
      width: 180,
      render: (value: number, record: CutFillRow) => (
        <NumberInput
          value={value}
          onChange={(newValue) =>
            handleInputChange(record.key, "topsoilDisposalOffsite", newValue)
          }
          decimalPlaces={3}
          placeholder="100"
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 110,
      fixed: "right" as const,
      align: "center" as const,
      render: (_, record: CutFillRow) => (
        <Button
          type="text"
          danger
          icon={
            <DeleteOutlined style={{ color: "#ff4d4f", fontSize: "16px" }} />
          }
          onClick={() => deleteRow(record.key)}
          size="small"
        />
      ),
    },
  ], [baseColumns, formatColumnTitle, getColumnTitle, handleInputChange, deleteRow]);

  const cutFillColumns = React.useMemo(() => [
    ...baseColumns,
    {
      title: "Founds",
      children: [
        {
          title: formatColumnTitle(
            "Excavate to reduce level and deposit on site m³",
          ),
          dataIndex: "cutFounds",
          key: "cutFounds",
          width: getRecommendedColumnWidth("cutFounds"),
          render: (value: number, record: CutFillRow) => (
            <NumberInput
              value={value}
              onChange={(newValue) =>
                handleInputChange(record.key, "cutFounds", newValue)
              }
              decimalPlaces={3}
              placeholder="100"
            />
          ),
        },
        {
          title: formatColumnTitle(
            "Fill to make up levels with site won sub-soil m³",
          ),
          dataIndex: "fillFounds",
          key: "fillFounds",
          width: getRecommendedColumnWidth("fillFounds"),
          render: (value: number, record: CutFillRow) => (
            <NumberInput
              value={value}
              onChange={(newValue) =>
                handleInputChange(record.key, "fillFounds", newValue)
              }
              decimalPlaces={3}
              placeholder="100"
            />
          ),
        },
      ],
    },
    {
      title: "Paths",
      children: [
        {
          title: formatColumnTitle(
            "Excavate to reduce level and deposit on site m³",
          ),
          dataIndex: "cutPaths",
          key: "cutPaths",
          width: getRecommendedColumnWidth("cutPaths"),
          render: (value: number, record: CutFillRow) =>
            record.plotType === "garage" ? null : (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  handleInputChange(record.key, "cutPaths", newValue)
                }
                decimalPlaces={3}
                placeholder="100"
              />
            ),
        },
        {
          title: formatColumnTitle(
            "Fill to make up levels with site won sub-soil m³",
          ),
          dataIndex: "fillPaths",
          key: "fillPaths",
          width: getRecommendedColumnWidth("fillPaths"),
          render: (value: number, record: CutFillRow) =>
            record.plotType === "garage" ? null : (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  handleInputChange(record.key, "fillPaths", newValue)
                }
                decimalPlaces={3}
                placeholder="100"
              />
            ),
        },
      ],
    },
    {
      title: "Garden",
      children: [
        {
          title: formatColumnTitle(
            "Excavate to reduce level and deposit on site m³",
          ),
          dataIndex: "cutGarden",
          key: "cutGarden",
          width: getRecommendedColumnWidth("cutGarden"),
          render: (value: number, record: CutFillRow) =>
            record.plotType === "garage" ? null : (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  handleInputChange(record.key, "cutGarden", newValue)
                }
                decimalPlaces={3}
                placeholder="100"
              />
            ),
        },
        {
          title: formatColumnTitle(
            "Fill to make up levels with site won sub-soil m³",
          ),
          dataIndex: "fillGarden",
          key: "fillGarden",
          width: getRecommendedColumnWidth("fillGarden"),
          render: (value: number, record: CutFillRow) =>
            record.plotType === "garage" ? null : (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  handleInputChange(record.key, "fillGarden", newValue)
                }
                decimalPlaces={3}
                placeholder="100"
              />
            ),
        },
      ],
    },
    {
      title: "Drives",
      children: [
        {
          title: formatColumnTitle(
            "Excavate to reduce level and deposit on site m³",
          ),
          dataIndex: "cutDrives",
          key: "cutDrives",
          width: getRecommendedColumnWidth("cutDrives"),
          render: (value: number, record: CutFillRow) =>
            record.plotType === "garage" ? null : (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  handleInputChange(record.key, "cutDrives", newValue)
                }
                decimalPlaces={3}
                placeholder="100"
              />
            ),
        },
        {
          title: formatColumnTitle(
            "Fill to make up levels with site won sub-soil m³",
          ),
          dataIndex: "fillDrives",
          key: "fillDrives",
          width: getRecommendedColumnWidth("fillDrives"),
          render: (value: number, record: CutFillRow) =>
            record.plotType === "garage" ? null : (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  handleInputChange(record.key, "fillDrives", newValue)
                }
                decimalPlaces={3}
                placeholder="100"
              />
            ),
        },
        {
          title: formatColumnTitle("Fill to make up levels with 6F2 m³"),
          dataIndex: "fillToMakeUp6F2",
          key: "fillToMakeUp6F2",
          width: 180,
          render: (value: number, record: CutFillRow) =>
            record.plotType === "garage" ? null : (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  handleInputChange(record.key, "fillToMakeUp6F2", newValue)
                }
                decimalPlaces={3}
                placeholder="100"
              />
            ),
        },
      ],
    },
    {
      title: "Actions",
      key: "actions",
      width: 110,
      fixed: "right" as const,
      align: "center" as const,
      render: (_, record: CutFillRow) => (
        <Button
          type="text"
          danger
          icon={
            <DeleteOutlined style={{ color: "#ff4d4f", fontSize: "16px" }} />
          }
          onClick={() => deleteRow(record.key)}
          size="small"
        />
      ),
    },
  ], [baseColumns, formatColumnTitle, handleInputChange, deleteRow]);

  // addColumnCodes() is a pure function but was previously invoked inline in
  // JSX on every render; memoizing its result means it only re-runs when the
  // underlying column definitions actually change (same pattern applied to
  // PlotDrives.tsx's addColumnCodes call sites).
  const siteClearanceColumnsWithCodes = React.useMemo(
    () => addColumnCodes(siteClearanceColumns, ALL_REFERENCES),
    [siteClearanceColumns],
  );
  const cutFillColumnsWithCodes = React.useMemo(
    () => addColumnCodes(cutFillColumns, ALL_REFERENCES),
    [cutFillColumns],
  );

  // getScrollConfig(...) previously ran inline in JSX on every render,
  // returning a brand-new object each time - which would have defeated
  // CutFillsTableView's React.memo below (a new `scroll` object reference on
  // every render looks like a prop change, even when the actual width/height
  // it describes hasn't). Memoized per table off the only things that can
  // actually change its result.
  const siteClearanceScrollConfig = React.useMemo(
    () => getScrollConfig(siteClearanceColumns),
    [siteClearanceColumns, data.length],
  );
  const cutFillScrollConfig = React.useMemo(
    () => getScrollConfig(cutFillColumns),
    [cutFillColumns, data.length],
  );

  // Same reasoning as above: `summary` was an inline arrow function created
  // fresh every render. useCallback keeps it stable across renders that don't
  // touch `totals`/`filteredData.length`, so it doesn't defeat React.memo.
  const renderSiteClearanceSummary = React.useCallback(() => {
    if (filteredData.length === 0) return null;
    return (
      <Table.Summary fixed="bottom">
        <Table.Summary.Row style={{ backgroundColor: "#FFF7E6", fontWeight: 600 }}>
          <Table.Summary.Cell index={0} colSpan={4} align="center">
            <span style={{ color: "#000" }}>Totals</span>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={4} align="right">
            <span style={{ color: "#000", textAlign: "right", paddingRight: "8px" }}>
              {totals.vegStrip.toFixed(3)}
            </span>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={5} align="right">
            <span style={{ color: "#000", textAlign: "right", paddingRight: "8px" }}>
              {totals.topsoilStrip.toFixed(3)}
            </span>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={6} align="right">
            <span style={{ color: "#000", textAlign: "right", paddingRight: "8px" }}>
              {totals.topsoilDisposalOnsite.toFixed(3)}
            </span>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={7} align="right">
            <span style={{ color: "#000", textAlign: "right", paddingRight: "8px" }}>
              {totals.topsoilDisposalOffsite.toFixed(3)}
            </span>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={8} />
        </Table.Summary.Row>
      </Table.Summary>
    );
  }, [filteredData.length, totals]);

  const renderCutFillSummary = React.useCallback(() => {
    if (filteredData.length === 0) return null;
    return (
      <Table.Summary fixed="bottom">
        <Table.Summary.Row style={{ backgroundColor: "#FFF7E6", fontWeight: 600 }}>
          <Table.Summary.Cell index={0} colSpan={4} align="center">
            <span style={{ color: "#000" }}>Totals</span>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={4} align="right">
            <span style={{ color: "#000", textAlign: "right", paddingRight: "8px" }}>
              {totals.cutFounds.toFixed(3)}
            </span>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={5} align="right">
            <span style={{ color: "#000", textAlign: "right", paddingRight: "8px" }}>
              {totals.fillFounds.toFixed(3)}
            </span>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={6} align="right">
            <span style={{ color: "#000", textAlign: "right", paddingRight: "8px" }}>
              {totals.cutPaths.toFixed(3)}
            </span>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={7} align="right">
            <span style={{ color: "#000", textAlign: "right", paddingRight: "8px" }}>
              {totals.fillPaths.toFixed(3)}
            </span>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={8} align="right">
            <span style={{ color: "#000", textAlign: "right", paddingRight: "8px" }}>
              {totals.cutGarden.toFixed(3)}
            </span>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={9} align="right">
            <span style={{ color: "#000", textAlign: "right", paddingRight: "8px" }}>
              {totals.fillGarden.toFixed(3)}
            </span>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={10} align="right">
            <span style={{ color: "#000", textAlign: "right", paddingRight: "8px" }}>
              {totals.cutDrives.toFixed(3)}
            </span>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={11} align="right">
            <span style={{ color: "#000", textAlign: "right", paddingRight: "8px" }}>
              {totals.fillDrives.toFixed(3)}
            </span>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={12} align="right">
            <span style={{ color: "#000", textAlign: "right", paddingRight: "8px" }}>
              {totals.fillToMakeUp6F2.toFixed(3)}
            </span>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={13} />
        </Table.Summary.Row>
      </Table.Summary>
    );
  }, [filteredData.length, totals]);

  const handleSaveAndExit = async () => {
    try {
      message.loading({ content: "Saving tender...", key: "saveTender", duration: 0 });

      // Update tender state with hook values
      updateField("groundworks", {
        ...tender.groundworks,
        topsoilTableData: { onsite: topsoilOnsite, offsite: topsoilOffsite },
        soilManagementData: { ...soilManagementData },
        adjustments: {
          preliminaries: groundworksTotals.preliminariesAmount,
          ohp: groundworksTotals.ohpAmount,
          discount: groundworksTotals.discountAmount,
          includePreliminaries: groundworksTotals.preliminariesPercent > 0,
          includeOhp: groundworksTotals.ohpPercent > 0,
          includeDiscount: groundworksTotals.discountPercent > 0,
        },
        totals: {
          ...tender.groundworks?.totals,
          grandTotal: groundworksTotals.grandTotal,
          totalWithAdjustments: groundworksTotals.tenderTotal,
          tenderTotal: groundworksTotals.tenderTotal, // FINAL total
        },
      });

      // Update global settings with percentages
      updateField("globalSettings", {
        ...tender.globalSettings,
        groundworks: {
          overheadAndProfit: groundworksTotals.ohpPercent,
          preliminaries: groundworksTotals.preliminariesPercent,
          discount: groundworksTotals.discountPercent,
        },
      });

      updateField("status", "In-progress");
      updateField("lastModified", new Date());

      // Save to localStorage
      await saveTender();
      await new Promise((resolve) => setTimeout(resolve, 200));

      // Call both BOQ and BOQ-versions endpoints
      await forceSaveBothBoqEndpoints(
        tender,
        "Save and exit from Cut-Fills",
        {
          grandTotal: groundworksTotals.grandTotal,
          grandTotalWithAdjustments:
            groundworksTotals.grandTotalWithAdjustments,
          preliminariesAmount: groundworksTotals.preliminariesAmount,
          ohpAmount: groundworksTotals.ohpAmount,
          discountAmount: groundworksTotals.discountAmount,
          tenderTotal: groundworksTotals.tenderTotal,
          preliminariesPercent: groundworksTotals.preliminariesPercent,
          ohpPercent: groundworksTotals.ohpPercent,
          discountPercent: groundworksTotals.discountPercent,
        },
        user?.name,
        itemLibraryData,
      );

      message.destroy("saveTender");

      // Redirect to home
      setTimeout(() => {
        navigate("/");
      }, 500);
    } catch (error: any) {
      console.error("❌ Error submitting tender:", error);
      message.error({
        content: `Failed to submit tender: ${
          error.response?.data?.message || error.message || "Unknown error"
        }`,
        key: "saveTender",
        duration: 5,
      });
    }
  };

  return (
    <MainLayout
      title="Cut & Fills"
      workflowType="groundworks"
      currentStep="cut-fills"
      previousStep=""
      nextStep="foundations"
      showFooter={false}
    >
      <Card
        className="shadow-sm cut-fills-card"
        bodyStyle={{ padding: 0 }}
        style={{ borderRadius: "8px", overflow: "hidden" }}
      >
        {/* Header with tabs - Sticky together */}
        <div
          className="cut-fills-sticky-header"
          style={{
            position: "sticky",
            top: "144px",
            backgroundColor: "#fff",
            zIndex: 98,
            borderRadius: "8px 8px 0 0",
            isolation: "isolate",
          }}
        >
          {/* Header section */}
          <div
            className="px-6 pt-6 pb-4"
            style={{
              borderBottom: "1px solid #e5e7eb",
            }}
          >
            <div className="flex justify-between items-center">
              <div>
                <Title
                  level={3}
                  style={{
                    fontWeight: 600,
                    fontSize: "20px",
                    color: "#111827",
                    marginBottom: "8px",
                  }}
                >
                  Site clearance, Cut & Fill & Removals of Surplus Arisings
                </Title>
                <Text
                  type="secondary"
                  style={{ fontSize: "14px", color: "#6B7280" }}
                >
                  Enter the cut and fill measurements for each plot. Check these
                  volumes to ensure correct Nr plots.
                </Text>
              </div>
              {(activeTab === "site-clearance" || activeTab === "soil-management") && (
                <Button
                  icon={<SettingOutlined />}
                  onClick={() =>
                    activeTab === "site-clearance"
                      ? setConfigModalVisible(true)
                      : setSoilConfigModalVisible(true)
                  }
                  type="default"
                  style={{
                    height: "32px",
                    borderRadius: "6px",
                    fontWeight: 500,
                    borderColor: "#1890ff",
                    color: "#1890ff",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    flexShrink: 0,
                    marginTop: "18px",
                  }}
                  className="configure-headers-btn"
                >
                  Configure Headers
                </Button>
              )}
            </div>
          </div>

          {/* Plot Type Filter Dropdown — hidden on the Soil Management tab,
              which renders aggregate-only tables and isn't tied to per-plot rows. */}
          {activeTab !== "soil-management" && (
            <div className="px-6" style={{ paddingTop: "16px", paddingBottom: "0px" }}>
              <div className="flex items-center gap-2">
                <Text style={{ fontWeight: 500 }}>View:</Text>
                <Select
                  value={plotTypeFilter}
                  onChange={setPlotTypeFilter}
                  style={{ width: 220 }}
                  size="middle"
                  className="supplier-select"
                >
                  <Select.Option value="plots">Plots</Select.Option>
                  <Select.Option value="garages">Garages</Select.Option>
                </Select>
              </div>
            </div>
          )}

          {/* Tab Buttons - Part of sticky header */}
          <div
            className="cut-fills-sticky-tabs px-6"
            style={{
              paddingTop: "24px",
              paddingBottom: "0px",
              display: "flex",
              gap: "4px",
              backgroundColor: "#fff",
            }}
          >
            <Button
              type={activeTab === "site-clearance" ? "primary" : "default"}
              onClick={() => setActiveTab("site-clearance")}
              className={
                activeTab === "site-clearance"
                  ? "active-tab-button"
                  : "inactive-tab-button"
              }
              style={{
                backgroundColor:
                  activeTab === "site-clearance" ? COLORS.primary : "#E1E6EF",
                borderColor:
                  activeTab === "site-clearance" ? COLORS.primary : "#E1E6EF",
                color: activeTab === "site-clearance" ? "#fff" : "#6B7280",
                fontWeight: 500,
                borderRadius: "6px 6px 0 0",
                height: "38px",
                paddingLeft: "20px",
                paddingRight: "20px",
                borderBottom:
                  activeTab === "site-clearance" ? "none" : "1px solid #E1E6EF",
                position: "relative",
                zIndex: activeTab === "site-clearance" ? 2 : 1,
              }}
            >
              Site Clearance
            </Button>
            <Button
              type={activeTab === "cut-fill" ? "primary" : "default"}
              onClick={() => setActiveTab("cut-fill")}
              className={
                activeTab === "cut-fill"
                  ? "active-tab-button"
                  : "inactive-tab-button"
              }
              style={{
                backgroundColor:
                  activeTab === "cut-fill" ? COLORS.primary : "#E1E6EF",
                borderColor:
                  activeTab === "cut-fill" ? COLORS.primary : "#E1E6EF",
                color: activeTab === "cut-fill" ? "#fff" : "#6B7280",
                fontWeight: 500,
                borderRadius: "6px 6px 0 0",
                height: "38px",
                paddingLeft: "20px",
                paddingRight: "20px",
                borderBottom:
                  activeTab === "cut-fill" ? "none" : "1px solid #E1E6EF",
                position: "relative",
                zIndex: activeTab === "cut-fill" ? 2 : 1,
              }}
            >
              Cut & Fill
            </Button>
            <Button
              type={activeTab === "soil-management" ? "primary" : "default"}
              onClick={() => setActiveTab("soil-management")}
              className={
                activeTab === "soil-management"
                  ? "active-tab-button"
                  : "inactive-tab-button"
              }
              style={{
                backgroundColor:
                  activeTab === "soil-management" ? COLORS.primary : "#E1E6EF",
                borderColor:
                  activeTab === "soil-management" ? COLORS.primary : "#E1E6EF",
                color: activeTab === "soil-management" ? "#fff" : "#6B7280",
                fontWeight: 500,
                borderRadius: "6px 6px 0 0",
                height: "38px",
                paddingLeft: "20px",
                paddingRight: "20px",
                borderBottom:
                  activeTab === "soil-management" ? "none" : "1px solid #E1E6EF",
                position: "relative",
                zIndex: activeTab === "soil-management" ? 2 : 1,
              }}
            >
              Soil Management
            </Button>
            <div style={{ flex: 1 }} />
            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
              {(activeTab === "site-clearance" || activeTab === "cut-fill") &&
                selectedCutFillKeys.length > 0 && (
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={deleteSelectedRows}
                    style={{
                      height: "32px",
                      borderRadius: "6px",
                      fontWeight: 500,
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    Delete Selected ({selectedCutFillKeys.length})
                  </Button>
                )}
            </div>
          </div>
        </div>

        {/* Content section - Tables */}
        <div
          className="px-6 pb-6 cut-fills-content-section"
          style={{ paddingTop: "0px", position: "relative", zIndex: 1 }}
        >
          {/* Site Clearance Table - stays mounted once visited; toggled via
              CSS display instead of unmount/remount (see mountedTabs above).
              Wrapped in the memoized CutFillsTableView so a plain tab switch
              (activeTab changing, nothing else) skips reconciling this
              table's rows entirely when it's the one being hidden. */}
          {mountedTabs.has("site-clearance") && (
          <div style={{ display: activeTab === "site-clearance" ? undefined : "none" }}>
            <CutFillsTableView
              isLoading={isSiteClearanceTableLoading}
              dataSource={filteredData}
              columns={siteClearanceColumnsWithCodes}
              rowSelection={rowSelectionConfig}
              scroll={siteClearanceScrollConfig}
              sticky={siteClearanceSticky}
              renderSummary={renderSiteClearanceSummary}
            />
          </div>
          )}

          {/* Cut & Fill Table - stays mounted once visited; toggled via CSS
              display instead of unmount/remount (see mountedTabs above).
              Wrapped in the memoized CutFillsTableView so a plain tab switch
              (activeTab changing, nothing else) skips reconciling this
              table's rows entirely when it's the one being hidden. */}
          {mountedTabs.has("cut-fill") && (
          <div style={{ display: activeTab === "cut-fill" ? undefined : "none" }}>
            <CutFillsTableView
              isLoading={isCutFillTableLoading}
              dataSource={filteredData}
              columns={cutFillColumnsWithCodes}
              rowSelection={rowSelectionConfig}
              scroll={cutFillScrollConfig}
              sticky={cutFillSticky}
              renderSummary={renderCutFillSummary}
            />
          </div>
          )}

          {/* Soil Management Tab - Topsoil + Subsoil Tables */}
          {activeTab === "soil-management" && (() => {
            const thStyle: React.CSSProperties = {
              backgroundColor: COLORS.tableHeaderBg,
              fontWeight: 600,
              color: "#000",
              padding: "12px 16px",
              textAlign: "center",
              fontSize: "13px",
              borderRight: "1px solid #e5e7eb",
              borderBottom: "1px solid #e5e7eb",
            };
            const tdLabelStyle: React.CSSProperties = {
              padding: "10px 16px",
              borderRight: "1px solid #e5e7eb",
              borderBottom: "1px solid #e5e7eb",
              fontSize: "14px",
            };
            const tdValStyle: React.CSSProperties = {
              padding: "10px 16px",
              textAlign: "right",
              borderRight: "1px solid #e5e7eb",
              borderBottom: "1px solid #e5e7eb",
              fontSize: "14px",
            };
            const totalRowStyle: React.CSSProperties = {
              backgroundColor: "#FFF7E6",
              fontWeight: 600,
            };
            const sectionHeadingStyle: React.CSSProperties = {
              fontWeight: 600,
              fontSize: "14px",
              color: "#374151",
              marginTop: "20px",
              marginBottom: "8px",
            };

            // Topsoil calculations
            const topsoilBalance = Math.round((topsoilOnsite - topsoilOffsite) * 1000) / 1000;

            // Subsoil calculations — first 4 rows auto-computed from cutFillsTable via effectiveSoilManagementData
            type SubsoilRow = {
              label: string;
              cutField: keyof SoilManagementData;
              fillField: keyof SoilManagementData;
              autoKeys?: { cut: 'cutFounds'|'cutPaths'|'cutGarden'|'cutDrives'; fill: 'fillFounds'|'fillPaths'|'fillGarden'|'fillDrives' };
              autoModal?: 'foundationsArisings' | 'plotDrainage' | 'walls' | 'plotServices';
            };
            const soilRows: SubsoilRow[] = [
              { label: "Foundations C&F", cutField: "foundationsCutArisings", fillField: "foundationsFill", autoKeys: { cut: 'cutFounds', fill: 'fillFounds' } },
              { label: "Slab Paths", cutField: "slabPathsCut", fillField: "slabPathsFill", autoKeys: { cut: 'cutPaths', fill: 'fillPaths' } },
              { label: "Gardens", cutField: "gardensCut", fillField: "gardensFill", autoKeys: { cut: 'cutGarden', fill: 'fillGarden' } },
              { label: "Drives", cutField: "drivesCut", fillField: "drivesFill", autoKeys: { cut: 'cutDrives', fill: 'fillDrives' } },
              { label: "Foundations Arisings", cutField: "foundationsArisingsCut", fillField: "foundationsArisingsFill", autoModal: 'foundationsArisings' },
              { label: "Plot Drainage", cutField: "plotDrainageCut", fillField: "plotDrainageFill", autoModal: 'plotDrainage' },
              { label: "Walls", cutField: "wallsCut", fillField: "wallsFill", autoModal: 'walls' },
              { label: "Plot Services", cutField: "plotServicesCut", fillField: "plotServicesFill", autoModal: 'plotServices' },
            ];
            const round2 = (n: number) => Math.round(n * 100) / 100;
            const totalCut = soilRows.reduce((sum, r) => sum + (effectiveSoilManagementData[r.cutField] || 0), 0);
            const totalFill = soilRows.reduce((sum, r) => sum + (effectiveSoilManagementData[r.fillField] || 0), 0);
            const subsoilBalance = round2(totalCut - totalFill);

            const renderBalanceBadge = (balance: number) => {
              if (balance > 0) return <Tag color="green" style={{ fontWeight: 600, fontSize: "12px" }}>Surplus</Tag>;
              if (balance < 0) return <Tag color="red" style={{ fontWeight: 600, fontSize: "12px" }}>Deficit</Tag>;
              return null;
            };

            return (
              <div style={{ marginTop: "16px" }}>
                {/* ── Topsoil Table ─────────────────────────────────────── */}
                <div style={sectionHeadingStyle}>Topsoil</div>
                <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #e5e7eb" }}>
                  <thead>
                    <tr>
                      <th style={{ ...thStyle, textAlign: "left", width: "40%" }}></th>
                      <th style={thStyle}>Onsite (m³)</th>
                      <th style={thStyle}>Offsite (m³)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={tdLabelStyle}>Topsoil Disposal</td>
                      <td style={tdValStyle}>{topsoilOnsite.toFixed(3)}</td>
                      <td style={tdValStyle}>{topsoilOffsite.toFixed(3)}</td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr>
                      <td style={{ ...tdLabelStyle, fontWeight: 700, borderBottom: "none" }}>Balance</td>
                      <td
                        colSpan={2}
                        style={{ ...tdValStyle, fontWeight: 700, borderBottom: "none", borderRight: "none" }}
                      >
                        <span style={{ marginRight: "10px" }}>{topsoilBalance.toFixed(3)}</span>
                        {renderBalanceBadge(topsoilBalance)}
                      </td>
                    </tr>
                  </tfoot>
                </table>

                {/* ── Subsoil Table ─────────────────────────────────────── */}
                <div style={sectionHeadingStyle}>Subsoil</div>
                <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #e5e7eb" }}>
                  <thead>
                    <tr>
                      <th style={{ ...thStyle, textAlign: "left", width: "40%" }}></th>
                      <th style={thStyle}>Cut / Arisings (m³)</th>
                      <th style={thStyle}>Fill (m³)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {soilRows.map((row) => (
                      <tr key={row.label}>
                        <td style={tdLabelStyle}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            {row.label}
                            {row.autoKeys && (
                              <InfoCircleOutlined
                                style={{ color: '#1677ff', cursor: 'pointer', fontSize: 13 }}
                                onClick={() => setSubsoilFormulaModal({
                                  visible: true,
                                  label: row.label,
                                  cutField: row.cutField,
                                  fillField: row.fillField,
                                  rowKey: row.autoKeys!.cut,
                                  fillRowKey: row.autoKeys!.fill,
                                })}
                              />
                            )}
                            {row.autoModal && (
                              <InfoCircleOutlined
                                style={{ color: '#1677ff', cursor: 'pointer', fontSize: 13 }}
                                onClick={() => setSubsoilDetailModal(row.autoModal!)}
                              />
                            )}
                          </span>
                        </td>
                        <td style={tdValStyle}>{round2(effectiveSoilManagementData[row.cutField] || 0).toFixed(2)}</td>
                        <td style={tdValStyle}>{round2(effectiveSoilManagementData[row.fillField] || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={totalRowStyle}>
                      <td style={{ ...tdLabelStyle, ...totalRowStyle }}>Total</td>
                      <td style={{ ...tdValStyle, ...totalRowStyle }}>{round2(totalCut).toFixed(2)}</td>
                      <td style={{ ...tdValStyle, ...totalRowStyle }}>{round2(totalFill).toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td style={{ ...tdLabelStyle, fontWeight: 700, borderBottom: "none" }}>Balance</td>
                      <td
                        colSpan={2}
                        style={{ ...tdValStyle, fontWeight: 700, borderBottom: "none", borderRight: "none" }}
                      >
                        <span style={{ marginRight: "10px" }}>{subsoilBalance.toFixed(2)}</span>
                        {renderBalanceBadge(subsoilBalance)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            );
          })()}

          {/* Add New Row Button — only for plot-by-plot tabs */}
          <div style={{ marginTop: "16px", display: activeTab === "soil-management" ? "none" : "block" }}>
            <Button
              type="text"
              icon={<PlusOutlined style={{ color: COLORS.primary }} />}
              onClick={addRow}
              style={{
                color: COLORS.primary,
                fontWeight: 500,
                padding: "4px 8px",
              }}
            >
              Add New Row
            </Button>
          </div>
        </div>

        {/* Footer with action buttons */}
        <div style={{ borderTop: "1px solid #e5e7eb" }}>
          <div className="px-6 py-4 flex justify-end gap-3">
            <Button
              onClick={handleSaveAndExit}
              style={{
                borderColor: COLORS.primary,
                color: COLORS.primary,
              }}
            >
              Save and Exit
            </Button>
            <Button
              type="primary"
              onClick={handleSaveAndContinue}
              style={{
                backgroundColor: COLORS.primary,
                borderColor: COLORS.primary,
              }}
            >
              Save & Continue <ArrowRightOutlined />
            </Button>
          </div>
        </div>

        {/* Excel Import Modal - Hidden, only for modal functionality */}
        <div style={{ display: "none" }}>
          <ExcelImportExport
            onExport={handleExportExcel}
            onImport={handleImportExcel}
            onDownloadTemplate={handleDownloadTemplate}
            importLoading={importLoading}
            importModalVisible={importModalVisible}
            setImportModalVisible={setImportModalVisible}
            hasData={data.length > 0}
          />
        </div>
      </Card>

      <style>{`
        .cut-fills-card.ant-card {
          border-radius: 8px !important;
          overflow: visible !important;
        }

        .cut-fills-card.ant-card .ant-card-body {
          border-radius: 8px !important;
          overflow: visible !important;
          position: relative !important;
        }

        .cut-fills-sticky-header {
          box-shadow: none;
          will-change: transform;
          transform: translateZ(0);
          z-index: 98 !important;
        }

        .cut-fills-sticky-tabs {
          border-bottom: none;
          min-height: 40px;
        }

        /* Ensure MainLayout doesn't interfere with positioning */
        .cut-fills-card {
          position: relative;
          height: auto;
          overflow: visible !important;
        }

        .cut-fills-card .ant-card-body {
          overflow: visible !important;
        }

        .cut-fills-content-section {
          clip-path: inset(0px -100vw -100vh -100vw);
        }

        /* Ensure table wrapper allows horizontal scroll with sticky headers */
        .cut-fills-table .ant-table-body {
          overflow-x: auto !important;
          margin-top: 0 !important;
        }

        .active-tab-button {
          border-bottom: none !important;
        }

        .inactive-tab-button {
          border-bottom: none !important;
        }

        .cut-fills-table {
          font-size: 14px;
          margin-top: 0 !important;
        }

        .cut-fills-table .ant-table {
          border: none !important;
          margin-top: 0 !important;
        }

        .cut-fills-table .ant-table-container {
          border: 1px solid #e5e7eb !important;
          border-top: none !important;
          border-radius: 0 !important;
        }

        .cut-fills-table .ant-table-thead > tr > th {
          min-width: 110px !important;
          background-color: ${COLORS.tableHeaderBg} !important;
          font-weight: 600 !important;
          color: #000 !important;
          border-right: 1px solid #e5e7eb !important;
          border-bottom: 1px solid #e5e7eb !important;
          border-left: none !important;
          border-top: none !important;
          padding: 16px 8px 10px 8px !important;
          text-align: center !important;
          font-size: 13px !important;
          will-change: transform !important;
          transform: translateZ(0) !important;
          z-index: 97 !important;
          vertical-align: top !important;
          overflow: hidden !important;
        }

        /* Wrap header titles instead of truncating them — long labels like
           "Site strip to remove vegetation"/"Excavate to strip topsoil, depth
           n.e. xxmm" were being ellipsis-truncated to a single line and only
           readable via the Tooltip hover, same fix as index.css's global
           .gw-header-title rule (this page's local overrides were re-forcing
           single-line truncation with !important, taking precedence over it). */
        .cut-fills-table .ant-table-thead > tr > th .text-center {
          width: 100% !important;
        }

        .cut-fills-table .ant-table-thead > tr > th .gw-header-title {
          white-space: normal !important;
          word-break: break-word !important;
          display: block !important;
          width: 100% !important;
        }

        /* Make Tooltip wrapper span block-level so the inner title div wraps predictably */
        .cut-fills-table .ant-table-thead > tr > th span {
          display: block !important;
          max-width: 100% !important;
        }

        .cut-fills-table .ant-table-thead > tr > th:first-child {
          border-left: none !important;
        }

        .cut-fills-table .ant-table-thead > tr > th.ant-table-cell-fix-left {
          z-index: 99 !important;
          background-color: ${COLORS.tableHeaderBg} !important;
        }

        .cut-fills-table .ant-table-tbody > tr > td.ant-table-cell-fix-left {
          z-index: 2 !important;
          background-color: #fff !important;
        }

        .cut-fills-table .ant-table-tbody > tr:hover > td.ant-table-cell-fix-left {
          background-color: #fafafa !important;
        }

        .cut-fills-table .ant-table-thead > tr > th.ant-table-cell-fix-right {
          z-index: 100 !important;
          background-color: ${COLORS.tableHeaderBg} !important;
        }

        .cut-fills-table .ant-table-tbody > tr > td.ant-table-cell-fix-right {
          z-index: 2 !important;
          background-color: #fff !important;
        }

        .cut-fills-table .ant-table-tbody > tr:hover > td.ant-table-cell-fix-right {
          background-color: #fafafa !important;
        }

        /* The generic min-width:110px above also matches the rowSelection
           checkbox column since it's a plain th/td. Keep it pinned to the
           rowSelection columnWidth instead of being stretched. */
        .cut-fills-table .ant-table-thead > tr > th.ant-table-selection-column,
        .cut-fills-table .ant-table-tbody > tr > td.ant-table-selection-column {
          min-width: 48px !important;
          width: 48px !important;
        }

        .cut-fills-table .ant-table-tbody > tr > td {
          min-width: 110px !important;
          padding: 6px 8px !important;
          border-right: 1px solid #e5e7eb !important;
          border-bottom: 1px solid #e5e7eb !important;
          border-left: none !important;
          border-top: none !important;
          background-color: #fff;
        }

        .cut-fills-table .ant-table-tbody > tr > td:first-child {
          border-left: none !important;
        }

        .cut-fills-table .ant-table-tbody > tr:hover > td {
          background-color: #fafafa !important;
        }

        /* Bulk-select checkboxes should not highlight the row — keep the normal/hover backgrounds */
        .cut-fills-table .ant-table-tbody > tr.ant-table-row-selected > td {
          background-color: #fff !important;
        }

        .cut-fills-table .ant-table-tbody > tr.ant-table-row-selected:hover > td {
          background-color: #fafafa !important;
        }

        .cut-fills-table .ant-input {
          border: 1px solid #d9d9d9 !important;
          border-radius: 4px !important;
          padding: 4px 11px !important;
          font-size: 14px !important;
          height: 32px !important;
        }

        .cut-fills-table .ant-input[type="text"] {
          text-align: center !important;
        }

        /* Right-align NumberInput components (text inputs used for numbers) */
        .cut-fills-table td:not(:nth-child(1)):not(:nth-child(2)):not(:nth-child(3)) .ant-input[type="text"] {
          text-align: right !important;
        }

        .cut-fills-table .ant-input[type="number"] {
          text-align: right !important;
        }

        .cut-fills-table .ant-input:focus {
          border-color: ${COLORS.primary} !important;
          box-shadow: 0 0 0 2px rgba(22, 126, 249, 0.1) !important;
        }

        .cut-fills-table .ant-input-number {
          width: 100%;
        }

        .cut-fills-table .ant-table-summary > tr > td {
          background-color: #FFF7E6 !important;
          border-right: 1px solid #e5e7eb !important;
          border-bottom: none !important;
          border-left: none !important;
          border-top: none !important;
          padding: 10px 8px !important;
          font-weight: 600 !important;
        }

        .cut-fills-table .ant-table-summary > tr > td:first-child {
          border-left: none !important;
        }

        .cut-fills-table .ant-btn-text.ant-btn-dangerous {
          color: #ff4d4f !important;
        }

        .cut-fills-table .ant-btn-text.ant-btn-dangerous:hover {
          background-color: #fff1f0 !important;
        }
      `}</style>

      <DeleteConfirmationModal
        visible={deleteModalVisible}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />

      {/* Column Configuration Modal — Site Clearance */}
      <ColumnConfigModal
        title="Configure Headers"
        open={configModalVisible}
        onClose={() => {
          setTempPrefixValues({ ...prefixValues });
          setConfigModalVisible(false);
        }}
        onApply={handleSiteClearanceConfigApply}
        width={500}
        isViewMode={tender.isViewMode}
      >
        <div className="grid grid-cols-1 gap-3">
          <div>
            <Text className="text-xs mb-1 block">
              Excavate to strip topsoil, depth n.e. xxmm
            </Text>
            <NumberInput
              value={(() => {
                const raw = parseFloat(tempPrefixValues["topsoilStrip"]) || 0;
                return raw >= 1 ? raw / 1000 : raw;
              })()}
              onChange={(value) => {
                const metresValue = value ? parseFloat(value.toString()) : 0;
                if (!metresValue) {
                  setTempPrefixValues((prev) => {
                    const newValues = { ...prev };
                    delete newValues["topsoilStrip"];
                    return newValues;
                  });
                } else {
                  const mmValue = String(Math.round(metresValue * 1000));
                  setTempPrefixValues((prev) => ({
                    ...prev,
                    topsoilStrip: mmValue,
                  }));
                }
              }}
              positiveOnly
              decimalPlaces={3}
              zeroIntegerOnly
            />
          </div>
        </div>
      </ColumnConfigModal>

      {/* Soil Management Configure Headers Modal */}
      <ColumnConfigModal
        title="Configure Headers"
        open={soilConfigModalVisible}
        onClose={() => {
          setTempTopsoilManagement(topsoilManagement);
          setTempTopsoilSubOption(topsoilSubOption);
          setTempSubsoilManagement(subsoilManagement);
          setTempSubsoilSubOption(subsoilSubOption);
          setTempSubsoilDisposalClassification(subsoilDisposalClassification);
          setSoilConfigModalVisible(false);
        }}
        onApply={handleSubsoilConfigApply}
        width={500}
        isViewMode={tender.isViewMode}
      >
        {/* Topsoil Management section */}
        <div className="grid grid-cols-1 gap-3">
          <Text strong className="text-xs text-gray-600 uppercase" style={{ display: "block", marginBottom: "4px" }}>
            Topsoil Management
          </Text>
          <div>
            <Text className="text-xs mb-1 block">Topsoil Management</Text>
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              height: "32px",
              padding: "4px 11px",
              border: "1px solid #d9d9d9",
              borderRadius: "6px",
              backgroundColor: "#f5f5f5",
              cursor: "not-allowed",
              userSelect: "none",
            }}>
              <span style={{ color: tempTopsoilManagement ? "rgba(0,0,0,0.88)" : "rgba(0,0,0,0.25)", fontSize: "14px" }}>
                {tempTopsoilManagement || "Select option"}
              </span>
              <svg viewBox="64 64 896 896" width="1em" height="1em" fill="rgba(0,0,0,0.25)" style={{ flexShrink: 0 }}>
                <path d="M884 256h-75c-5.1 0-9.9 2.5-12.9 6.6L512 654.2 227.9 262.6c-3-4.1-7.8-6.6-12.9-6.6h-75c-6.5 0-10.3 7.4-6.5 12.7l352.6 486.1c12.8 17.6 39 17.6 51.7 0l352.6-486.1c3.9-5.3.1-12.7-6.4-12.7z" />
              </svg>
            </div>
          </div>
          {tempTopsoilManagement && (
            <div>
              <Text className="text-xs mb-1 block">Sub-option</Text>
              <Select
                value={tempTopsoilSubOption || undefined}
                onChange={(val: string) => setTempTopsoilSubOption(val)}
                placeholder="Select sub-option"
                style={{ width: "100%" }}
                options={
                  tempTopsoilManagement === "Surplus"
                    ? [
                        { value: "Stockpile on site for re-use", label: "Stockpile on site for re-use" },
                        { value: "Removal surplus topsoil arisings from site", label: "Removal surplus topsoil arisings from site" },
                      ]
                    : [
                        { value: "Import clean topsoil", label: "Import clean topsoil" },
                        { value: "Excluded", label: "Excluded" },
                      ]
                }
              />
            </div>
          )}
        </div>

        {/* Subsoil Management section */}
        <div className="grid grid-cols-1 gap-3" style={{ borderTop: "1px solid #e8e8e8", marginTop: "16px", paddingTop: "16px" }}>
          <Text strong className="text-xs text-gray-600 uppercase" style={{ display: "block", marginBottom: "4px" }}>
            Subsoil Management
          </Text>
          <div>
            <Text className="text-xs mb-1 block">Subsoil Management</Text>
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              height: "32px",
              padding: "4px 11px",
              border: "1px solid #d9d9d9",
              borderRadius: "6px",
              backgroundColor: "#f5f5f5",
              cursor: "not-allowed",
              userSelect: "none",
            }}>
              <span style={{ color: tempSubsoilManagement ? "rgba(0,0,0,0.88)" : "rgba(0,0,0,0.25)", fontSize: "14px" }}>
                {tempSubsoilManagement || "Select option"}
              </span>
              <svg viewBox="64 64 896 896" width="1em" height="1em" fill="rgba(0,0,0,0.25)" style={{ flexShrink: 0 }}>
                <path d="M884 256h-75c-5.1 0-9.9 2.5-12.9 6.6L512 654.2 227.9 262.6c-3-4.1-7.8-6.6-12.9-6.6h-75c-6.5 0-10.3 7.4-6.5 12.7l352.6 486.1c12.8 17.6 39 17.6 51.7 0l352.6-486.1c3.9-5.3.1-12.7-6.4-12.7z" />
              </svg>
            </div>
          </div>
          {tempSubsoilManagement && (
            <div>
              <Text className="text-xs mb-1 block">Sub-option</Text>
              <Select
                value={tempSubsoilSubOption || undefined}
                onChange={(val: string) => {
                  setTempSubsoilSubOption(val);
                  setTempSubsoilDisposalClassification("");
                }}
                placeholder="Select sub-option"
                style={{ width: "100%" }}
                options={
                  tempSubsoilManagement === "Surplus"
                    ? [
                        { value: "Topsoil Disposal onsite", label: "Topsoil Disposal onsite" },
                        { value: "Remove surplus subsoil arisings offsite", label: "Remove surplus subsoil arisings offsite" },
                      ]
                    : [
                        { value: "Import clean subsoil", label: "Import clean subsoil" },
                        { value: "Excluded", label: "Excluded" },
                      ]
                }
              />
            </div>
          )}
          {tempSubsoilSubOption === "Remove surplus subsoil arisings offsite" && (
            <div>
              <Text className="text-xs mb-1 block">Disposal Classification</Text>
              <Select
                value={tempSubsoilDisposalClassification || undefined}
                onChange={(val: string) => setTempSubsoilDisposalClassification(val)}
                placeholder="Select classification"
                style={{ width: "100%" }}
                options={[
                  { value: "Inert", label: "Inert" },
                  { value: "Non Haz", label: "Non Haz" },
                  { value: "Haz", label: "Haz" },
                  { value: "Asbestos", label: "Asbestos" },
                ]}
              />
            </div>
          )}
        </div>
      </ColumnConfigModal>

      {/* Subsoil Formula Inspector Modal */}
      {subsoilFormulaModal?.visible && (() => {
        const { label, rowKey, fillRowKey } = subsoilFormulaModal;
        const rows = data.filter(r => r.plotNo !== 'TOTALS' && r.plotNo !== 'Total');
        const round3 = (n: number) => Math.round(n * 1000) / 1000;
        const totalCutVal = round3(rows.reduce((s, r) => s + (Number(r[rowKey]) || 0), 0));
        const totalFillVal = round3(rows.reduce((s, r) => s + (Number(r[fillRowKey]) || 0), 0));
        const tableData = rows.map((r, i) => ({
          key: String(i),
          plotNo: r.plotNo,
          housetype: r.housetype,
          cut: Number(r[rowKey]) || 0,
          fill: Number(r[fillRowKey]) || 0,
        })).filter(r => r.cut !== 0 || r.fill !== 0);
        return (
          <Modal
            title={`${label} — Formula Breakdown`}
            open
            onCancel={() => setSubsoilFormulaModal(null)}
            footer={[<Button key="close" onClick={() => setSubsoilFormulaModal(null)}>Close</Button>]}
            width={620}
          >
            <div style={{ fontSize: 13, color: '#374151' }}>
              <p style={{ margin: '0 0 12px' }}>
                Values are summed directly from every row in the Cut &amp; Fills table. Only rows with a non-zero value are shown.
              </p>
              <Table
                size="small"
                bordered
                pagination={false}
                scroll={{ y: 360 }}
                dataSource={tableData.length > 0 ? tableData : [{ key: 'empty', plotNo: '—', housetype: '—', cut: 0, fill: 0 }]}
                columns={[
                  { title: 'Plot No', dataIndex: 'plotNo', key: 'plotNo', width: 80 },
                  { title: 'House Type', dataIndex: 'housetype', key: 'housetype' },
                  { title: 'Cut / Arisings (m³)', dataIndex: 'cut', key: 'cut', align: 'right' as const, render: (v: number) => v.toFixed(3) },
                  { title: 'Fill (m³)', dataIndex: 'fill', key: 'fill', align: 'right' as const, render: (v: number) => v.toFixed(3) },
                ]}
                summary={() => (
                  <Table.Summary.Row style={{ backgroundColor: '#fafafa', fontWeight: 600 }}>
                    <Table.Summary.Cell index={0} colSpan={2} align="right"><Typography.Text strong>Total</Typography.Text></Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right"><Typography.Text strong>{totalCutVal.toFixed(3)}</Typography.Text></Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right"><Typography.Text strong>{totalFillVal.toFixed(3)}</Typography.Text></Table.Summary.Cell>
                  </Table.Summary.Row>
                )}
              />
            </div>
          </Modal>
        );
      })()}

      {/* ── Foundations Arisings detail modal ─────────────────────────────── */}
      {subsoilDetailModal === 'foundationsArisings' && (() => {
        const fndTable = (tender?.groundworks?.foundationsTable || []) as FoundationRow[];
        const round3 = (n: number) => Math.round(n * 1000) / 1000;
        const tableData = fndTable
          .map((r, i) => ({
            key: i,
            type: r.plotType === 'garage' ? 'Garage' : 'Plot',
            plotNo: r.plotNo,
            housetype: r.housetype,
            excavateConcreteDeposit: Number(r.stockpileArisings) || 0,
            abnormalsDeposit: Number(r.depositOnSite) || 0,
          }))
          .filter(r => r.excavateConcreteDeposit !== 0 || r.abnormalsDeposit !== 0);
        const sumFor = (rows: FoundationRow[], field: 'stockpileArisings' | 'depositOnSite') =>
          round3(rows.reduce((s, r) => s + (Number(r[field]) || 0), 0));
        const plotRows = fndTable.filter(r => r.plotType !== 'garage');
        const garageRows = fndTable.filter(r => r.plotType === 'garage');
        const totalExcavateConcreteDeposit = round3(sumFor(plotRows, 'stockpileArisings') + sumFor(garageRows, 'stockpileArisings'));
        const totalAbnormalsDeposit = round3(sumFor(plotRows, 'depositOnSite') + sumFor(garageRows, 'depositOnSite'));
        const plotTotal = round3(sumFor(plotRows, 'stockpileArisings') + sumFor(plotRows, 'depositOnSite'));
        const garageTotal = round3(sumFor(garageRows, 'stockpileArisings') + sumFor(garageRows, 'depositOnSite'));
        return (
          <Modal
            title="Foundations Arisings — Formula Breakdown"
            open
            onCancel={() => setSubsoilDetailModal(null)}
            footer={[<Button key="close" onClick={() => setSubsoilDetailModal(null)}>Close</Button>]}
            width={760}
          >
            <div style={{ fontSize: 13, color: '#374151' }}>
              <p style={{ margin: '0 0 8px' }}>
                <strong>Formula:</strong> Σ (Excavate &amp; Concrete → Deposit on site + Abnormals → Deposit on site) across all Foundations rows — Plots and Garages
              </p>
              <p style={{ margin: '0 0 12px', color: '#6b7280' }}>
                Source: Foundations table — depositOnSite (Excavate &amp; Concrete tab) + depositOnSite (Abnormals tab) per plot/garage row
              </p>
              <Table
                size="small"
                bordered
                pagination={false}
                scroll={{ y: 300 }}
                dataSource={tableData.length > 0 ? tableData : [{ key: 'empty', type: '—', plotNo: '—', housetype: '—', excavateConcreteDeposit: 0, abnormalsDeposit: 0 }]}
                columns={[
                  { title: 'Type', dataIndex: 'type', key: 'type', width: 70 },
                  { title: 'Plot No', dataIndex: 'plotNo', key: 'plotNo', width: 80 },
                  { title: 'House Type', dataIndex: 'housetype', key: 'housetype' },
                  { title: 'Excavate & Concrete → Deposit on site (m³)', dataIndex: 'excavateConcreteDeposit', key: 'excavateConcreteDeposit', align: 'right' as const, render: (v: number) => v.toFixed(3) },
                  { title: 'Abnormals → Deposit on site (m³)', dataIndex: 'abnormalsDeposit', key: 'abnormalsDeposit', align: 'right' as const, render: (v: number) => v.toFixed(3) },
                ]}
                summary={() => (
                  <Table.Summary.Row style={{ backgroundColor: '#fafafa', fontWeight: 600 }}>
                    <Table.Summary.Cell index={0} colSpan={3} align="right"><Typography.Text strong>Total</Typography.Text></Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right"><Typography.Text strong>{totalExcavateConcreteDeposit.toFixed(3)}</Typography.Text></Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="right"><Typography.Text strong>{totalAbnormalsDeposit.toFixed(3)}</Typography.Text></Table.Summary.Cell>
                  </Table.Summary.Row>
                )}
              />
              <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end', gap: 16, color: '#6b7280' }}>
                <span>Plot subtotal: {plotTotal.toFixed(3)} m³</span>
                <span>Garage subtotal: {garageTotal.toFixed(3)} m³</span>
              </div>
              <div style={{ marginTop: 8, textAlign: 'right', fontWeight: 600 }}>
                Total Cut: {effectiveSoilManagementData.foundationsArisingsCut.toFixed(3)} m³
              </div>
            </div>
          </Modal>
        );
      })()}

      {/* ── Plot Drainage detail modal ─────────────────────────────────────── */}
      {subsoilDetailModal === 'plotDrainage' && (() => {
        const gw = tender?.groundworks as unknown as Record<string, unknown> | undefined;
        const osmaConfig = (tender?.groundworks?.drainageDimensionValues || {}) as Record<string, string>;
        const polypipeConfig = ((gw?.drainagePolypipeConfig) || {}) as Record<string, string>;
        const osmaNrTakeOff = parseFloat(osmaConfig.foulNrPlotsTakeOff ?? '0') || 0;
        const osmaTotalPlots = parseFloat(osmaConfig.foulTotalPlots ?? '0') || 0;
        const ppNrTakeOff = parseFloat(polypipeConfig.nrPlotsTakeOff ?? '0') || 0;
        const ppTotalPlots = parseFloat(polypipeConfig.totalPlots ?? '0') || 0;
        const drainageRows = (tender?.groundworks?.drainageTableStandard || (gw?.drainageTable) || []) as Array<Record<string, unknown>>;
        const sumDrain = (f: string) => drainageRows.reduce((s, r) => s + (Number(r[f]) || 0), 0);
        const osmaTip = sumDrain('tipOnSite');
        const osmaStormTip = sumDrain('osmaStormTipOnsite');
        const ppTip = sumDrain('polypipeTipOnSite');
        const ppStormTip = sumDrain('polypipeStormTipOnSite');
        const round3 = (n: number) => Math.round(n * 1000) / 1000;
        const osmaResult = round3(osmaNrTakeOff > 0 ? (osmaTip + osmaStormTip) / osmaNrTakeOff * osmaTotalPlots : 0);
        const ppResult = round3(ppNrTakeOff > 0 ? (ppTip + ppStormTip) / ppNrTakeOff * ppTotalPlots : 0);
        const rows = [
          { key: 'tip', label: 'Foul Tip On Site (m³)', osma: osmaTip.toFixed(3), pp: ppTip.toFixed(3) },
          { key: 'storm', label: 'Storm Tip On Site (m³)', osma: osmaStormTip.toFixed(3), pp: ppStormTip.toFixed(3) },
          { key: 'nr', label: 'Nr Plots Taken Off', osma: String(osmaNrTakeOff), pp: String(ppNrTakeOff) },
          { key: 'total', label: 'Total Plots', osma: String(osmaTotalPlots), pp: String(ppTotalPlots) },
          { key: 'result', label: 'Result (m³)', osma: osmaResult.toFixed(3), pp: ppResult.toFixed(3) },
        ];
        return (
          <Modal
            title="Plot Drainage — Formula Breakdown"
            open
            onCancel={() => setSubsoilDetailModal(null)}
            footer={[<Button key="close" onClick={() => setSubsoilDetailModal(null)}>Close</Button>]}
            width={520}
          >
            <div style={{ fontSize: 13, color: '#374151' }}>
              <p style={{ margin: '0 0 8px' }}>
                <strong>Formula:</strong> (Foul + Storm Tip On Site ÷ Nr Plots Taken Off) × Total Plots
              </p>
              <p style={{ margin: '0 0 12px', color: '#6b7280' }}>
                Calculated separately for OSMA and Polypipe; results are summed.
              </p>
              <Table
                size="small"
                bordered
                pagination={false}
                dataSource={rows}
                columns={[
                  { title: 'Parameter', dataIndex: 'label', key: 'label' },
                  { title: 'OSMA', dataIndex: 'osma', key: 'osma', align: 'right' as const },
                  { title: 'Polypipe', dataIndex: 'pp', key: 'pp', align: 'right' as const },
                ]}
              />
              <div style={{ marginTop: 8, textAlign: 'right', fontWeight: 600 }}>
                Total Cut: {effectiveSoilManagementData.plotDrainageCut.toFixed(3)} m³
              </div>
            </div>
          </Modal>
        );
      })()}

      {/* ── Walls detail modal ────────────────────────────────────────────── */}
      {subsoilDetailModal === 'walls' && (() => {
        const gw2 = tender?.groundworks as unknown as Record<string, unknown> | undefined;
        // Retaining walls: read directly from API response (avoids dependency on WallsSpecificationContainer mount)
        const retApiSpecsModal = retWallsBuildUp?.specifications || [];
        const geoWallDims = ((gw2?.geoToberWallBuildUpDimensions) || {}) as Record<string, Array<{ description?: string; net: number }>>;
        const retWallRows = (tender?.groundworks?.retainingWallsTable || []) as Array<Record<string, unknown>>;
        const geoWallRows = (tender?.groundworks?.geoToberWallTable || []) as Array<Record<string, unknown>>;
        const round4 = (n: number) => Math.round(n * 10000) / 10000;
        const round3 = (n: number) => Math.round(n * 1000) / 1000;
        const netByDesc = (items: Array<{ description?: string; net: number }>, substr: string) =>
          items.filter(i => (i.description || '').toLowerCase().includes(substr.toLowerCase()))
               .reduce((s, i) => s + (i.net || 0), 0);

        const retWallNameMap = Object.fromEntries(wallSpecifications.map(s => [s.id, s.name]));
        const geoWallNameMap = Object.fromEntries(geoToberWallSpecifications.map(s => [s.id, s.name]));
        // TPCW slots have no fixed dimension — their real name ("TPCW 450mm")
        // is configured per-tender via GeoToberWall.tsx's Configure Headers
        // modal and persisted to geoToberWallColumnHeaders/DimensionValues,
        // keyed by bare "tpcw1".."tpcw8" (not the "tobermore_tpcw1"-prefixed
        // specId used here — same prefix-stripping pattern as
        // GeoToberWallSpecificationContainer.tsx's getTpcwLabel). Falls back
        // to the static "TPCW xxxxmm" placeholder for slots left unconfigured.
        const tpcwColumnHeaders = (gw2?.geoToberWallColumnHeaders || {}) as Record<string, string>;
        const tpcwDimensionValues = (gw2?.geoToberWallDimensionValues || {}) as Record<string, string>;
        const getGeoWallSpecName = (specId: string): string => {
          const tpcwMatch = specId.match(/^tobermore_(tpcw\d+)$/);
          if (tpcwMatch) {
            const key = tpcwMatch[1];
            if (tpcwColumnHeaders[key]) return tpcwColumnHeaders[key];
            if (tpcwDimensionValues[key]) return normaliseDecimalMmInLabel(`TPCW ${tpcwDimensionValues[key]}mm`);
          }
          return geoWallNameMap[specId] || specId.toUpperCase();
        };

        type WallRow = { key: number; spec: string; type: string; stockpile: number; totalNet: number; linearM: number; volume: number };
        const rows: WallRow[] = [];
        Object.entries(wallFieldToSpecId).forEach(([fieldName, specId]) => {
          const spec      = retApiSpecsModal.find(s => s.id === specId);
          const items     = spec?.items || [];
          const stockpile = round4(netByDesc(items, 'stockpile'));
          const totalNet  = stockpile;
          const linearM   = round3(retWallRows.reduce((s, r) => s + (Number(r[fieldName]) || 0), 0));
          rows.push({ key: rows.length, spec: retWallNameMap[specId] || specId.toUpperCase(), type: 'Retaining', stockpile, totalNet, linearM, volume: round3(linearM * totalNet) });
        });
        Object.entries(geoToberWallFieldToSpecId).forEach(([fieldName, specId]) => {
          const geoItems  = geoWallDims[specId] || [];
          const stockpile = round4(netByDesc(geoItems, 'stockpile'));
          const totalNet  = stockpile;
          const linearM   = round3(geoWallRows.reduce((s, r) => s + (Number(r[fieldName]) || 0), 0));
          rows.push({ key: rows.length, spec: getGeoWallSpecName(specId), type: 'Geo/Tober', stockpile, totalNet, linearM, volume: round3(linearM * totalNet) });
        });
        const tableData = rows.filter(r => r.totalNet !== 0 || r.linearM !== 0);
        const additionalExcavation = round3(retWallRows.reduce(
          (s, r) => s + (Number(r.screenWallsAdditionalExcavation) || 0), 0,
        ));
        return (
          <Modal
            title="Walls — Formula Breakdown"
            open
            onCancel={() => setSubsoilDetailModal(null)}
            footer={[<Button key="close" onClick={() => setSubsoilDetailModal(null)}>Close</Button>]}
            width={760}
          >
            <div style={{ fontSize: 13, color: '#374151' }}>
              <p style={{ margin: '0 0 6px' }}>
                <strong>Formula:</strong> Σ Stockpile Onsite net m³/m × Linear metres per specification, + Additional Excavation (m³, Ret & Screen Take off)
              </p>
              <Table
                size="small"
                bordered
                pagination={false}
                scroll={{ y: 340 }}
                dataSource={tableData.length > 0 ? tableData : [{ key: 0, spec: 'No data — import wall build-up first', type: '—', stockpile: 0, totalNet: 0, linearM: 0, volume: 0 }]}
                columns={[
                  { title: 'Specification', dataIndex: 'spec', key: 'spec', width: 130 },
                  { title: 'Type', dataIndex: 'type', key: 'type', width: 85 },
                  { title: 'Stockpile Onsite (m³/m)', dataIndex: 'stockpile', key: 'stockpile', align: 'right' as const, render: (v: number) => v.toFixed(4) },
                  { title: 'Total Net (m³/m)', dataIndex: 'totalNet', key: 'totalNet', align: 'right' as const, render: (v: number) => <Typography.Text strong>{v.toFixed(4)}</Typography.Text> },
                  { title: 'Linear m', dataIndex: 'linearM', key: 'linearM', align: 'right' as const, render: (v: number) => v.toFixed(3) },
                  { title: 'Volume m³', dataIndex: 'volume', key: 'volume', align: 'right' as const, render: (v: number) => <Typography.Text strong>{v.toFixed(3)}</Typography.Text> },
                ]}
                summary={() => (
                  <>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={5} align="right">Additional Excavation (Ret & Screen Take off)</Table.Summary.Cell>
                      <Table.Summary.Cell index={5} align="right">{additionalExcavation.toFixed(3)}</Table.Summary.Cell>
                    </Table.Summary.Row>
                    <Table.Summary.Row style={{ backgroundColor: '#fafafa', fontWeight: 600 }}>
                      <Table.Summary.Cell index={0} colSpan={5} align="right"><Typography.Text strong>Total</Typography.Text></Table.Summary.Cell>
                      <Table.Summary.Cell index={5} align="right"><Typography.Text strong>{effectiveSoilManagementData.wallsCut.toFixed(3)}</Typography.Text></Table.Summary.Cell>
                    </Table.Summary.Row>
                  </>
                )}
              />
            </div>
          </Modal>
        );
      })()}

      {/* ── Plot Services detail modal ─────────────────────────────────────── */}
      {subsoilDetailModal === 'plotServices' && (() => {
        const svcRows = (tender?.groundworks?.plotServiceTable || []) as Array<Record<string, unknown>>;
        const round3 = (n: number) => Math.round(n * 1000) / 1000;
        const tableData = svcRows
          .map((r, i) => ({
            key: i,
            plotNo: r.plotNo,
            plots: Number(r.plots) || 0,
            apartments: Number(r.apartments) || 0,
            volume: round3(((Number(r.plots) || 0) + (Number(r.apartments) || 0)) * 7),
          }))
          .filter(r => r.plots !== 0 || r.apartments !== 0);
        return (
          <Modal
            title="Plot Services — Formula Breakdown"
            open
            onCancel={() => setSubsoilDetailModal(null)}
            footer={[<Button key="close" onClick={() => setSubsoilDetailModal(null)}>Close</Button>]}
            width={520}
          >
            <div style={{ fontSize: 13, color: '#374151' }}>
              <p style={{ margin: '0 0 12px' }}>
                <strong>Formula:</strong> (Plots + Apartments) × 7 m³ per plot
              </p>
              <Table
                size="small"
                bordered
                pagination={false}
                scroll={{ y: 300 }}
                dataSource={tableData.length > 0 ? tableData : [{ key: 0, plotNo: '—', plots: 0, apartments: 0, volume: 0 }]}
                columns={[
                  { title: 'Plot No', dataIndex: 'plotNo', key: 'plotNo', width: 80 },
                  { title: 'Plots', dataIndex: 'plots', key: 'plots', align: 'right' as const },
                  { title: 'Apartments', dataIndex: 'apartments', key: 'apartments', align: 'right' as const },
                  { title: 'Volume m³', dataIndex: 'volume', key: 'volume', align: 'right' as const, render: (v: number) => v.toFixed(3) },
                ]}
                summary={() => (
                  <Table.Summary.Row style={{ backgroundColor: '#fafafa', fontWeight: 600 }}>
                    <Table.Summary.Cell index={0} colSpan={3} align="right"><Typography.Text strong>Total</Typography.Text></Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right"><Typography.Text strong>{effectiveSoilManagementData.plotServicesCut.toFixed(3)}</Typography.Text></Table.Summary.Cell>
                  </Table.Summary.Row>
                )}
              />
            </div>
          </Modal>
        );
      })()}
    </MainLayout>
  );
};

export default CutFills;
